import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom'
import {
	Autocomplete, Box, Button, Chip, Collapse, FormControlLabel,
	IconButton, LinearProgress, MenuItem, Pagination, TablePagination, Select, Stack, Switch,
	Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
	TextField, Typography,
} from '@mui/material'
import {
	AppDialog, AppDialogHeader, AppDialogBody, AppDialogFooter, AppDialogCancel, AppNote,
	AppFilterChip, AppFilterPanel, AppPillToggle, AppTableSkeleton, AppBulkEditAction, filterPanelGridSx, secondaryButtonSx, AppBrandMark,
} from '@components'
import { MbKpiCard, MbKpiRow, MbKpiSkeleton } from 'Pages/MyBusiness/_shared/Ui'
import { dialogBodySx } from 'Pages/DsRepricing/@components/dialogBodySx'
import { setupScopeSx } from 'Pages/DsRepricing/Setup/@components/setupScopeSx'
import { dsColorNew } from '@palette'
import { alpha } from '@mui/material/styles'
import { CheckCircleOutline as CheckIcon, InfoOutlined as InfoIcon, BlockOutlined as BlockIcon, CheckCircle as DoneIcon, CancelOutlined as DiscardIcon, ErrorOutline as ErrorIcon, HorizontalRule as RemoveIcon, SearchOff as SearchOffIcon, ExpandMore as ExpandMoreIcon, ChevronRight as ChevronRightIcon, FilterAltOutlined as FilterIcon, SearchOutlined as SearchIcon, ManageSearchOutlined as OpenSearchIcon, PlayCircleOutline as PlayIcon, PauseCircleOutline as PauseIcon, TuneOutlined as TuneIcon, EditOutlined as EditIcon, VerticalAlignBottomOutlined as CapIcon, ScienceOutlined as QuarantineIcon, PublishOutlined as PublishIcon, LinkOutlined as LinkIcon, RefreshOutlined as RetryIcon, LinkOffOutlined as BrokenIcon, WarningAmberRounded as WarningIcon } from 'Pages/DsRepricing/@components/icons'
import { toneChipSx } from 'Pages/DsRepricing/@components/tones'

import { useOfficeScope } from 'Pages/DsRepricing/Setup/@services/useOfficeScope'
import { useCategories } from 'Pages/DsRepricing/Setup/@services/useCategories'
import { AxisCatalogProvider, useAxisCatalog } from 'Pages/DsRepricing/Setup/@services/axes'
import { SiteCatalogProvider, useSiteCatalog } from 'Pages/DsRepricing/Setup/@services/sites'
import {
	retryPmsErrorRows, ackPmsErrorRows,
	ACTIVITY_EVENT_TYPES, canonicalEventType,
} from 'Pages/DsRepricing/Setup/@mocks/activityLog'
import { overridable, bypassesSentence } from 'Pages/DsRepricing/Activity/@services/override'
import { capKey, protectionNotes, crossingSentence } from 'Pages/DsRepricing/Activity/@services/dateProtection'
import { consistencyNotes } from 'Pages/DsRepricing/Activity/@services/consistencyCap'
import { conversionFactor, conversionNote } from 'Pages/DsRepricing/Activity/@services/markupUsed'
import { rowLabel as spreadRowLabel, warningLines as spreadWarningLines } from 'Pages/DsRepricing/Activity/@services/spreadVsOrder'
import {
	outrankedBy, outrankedRowParts, outrankedTitle, outrankedSentence, oneNetNote,
	wonOverEntries, wonOverSentence,
} from 'Pages/DsRepricing/Activity/@services/outranked'
import {
	useActivityFeed, useActivityCounts,
} from 'Pages/DsRepricing/Activity/@services/useActivity'
import {
	Surface, SurfaceHeader, EmptyState, HelpNote,
} from 'Pages/DsRepricing/Setup/@components/Surface'
import PageWrapper from 'Pages/@PageWrapper'
import { DS_REPRICING_ROUTES } from '@constants/routes'
import { inputSx, cellPadSx, headCellSx } from 'Pages/DsRepricing/Setup/@components/formStyles'
import DateRangeField from 'Pages/DsRepricing/@components/DateRangeField'
import DurationRangeField from 'Pages/DsRepricing/@components/DurationRangeField'
import { pastPresets, pickupPresets, fromIso } from 'Pages/DsRepricing/@services/dateRange'
import { format as formatDate } from 'date-fns'
import {
	readCell, writeCell, cellOf, cellSentence, cellRowLabel, windowKeys, DURATION_MIN, DURATION_MAX,
} from 'Pages/DsRepricing/Activity/@services/cellFilter'
import { ModeBanner, useRepricingMode } from 'Pages/DsRepricing/Setup/@components/ModeSwitch'
import { activityActions } from 'Pages/DsRepricing/Activity/@services/useActivity'
import useToast from '@hooks/useToast'
import useConfirm from 'Pages/DsRepricing/@components/useConfirm'
import { incompleteMarketCopy, incompleteMarketNoChange }
	from 'Pages/DsRepricing/Activity/@services/incompleteMarket'
import { excludedCopy, compareCopy, EXCLUDED_TOOLTIP } from 'Pages/DsRepricing/Activity/@services/scopeFilter'
import { discardedCopy, ownKindRowLabel, isOwnKindUnknown } from 'Pages/DsRepricing/Activity/@services/ownRows'
import { indexFaces, faceLabel, faceOf, noStrategyCopy } from 'Pages/DsRepricing/Activity/@services/faceScope'
import { setupApi } from 'Pages/DsRepricing/Setup/@services/api'
import { displayDay } from '@utils'
import { format } from 'date-fns'

const PAGE_SIZE = 30

// The outlined actions of the cards and the detail panels: the app's secondary button at the dialogs' 36px
const detailButtonSx = { ...secondaryButtonSx, height: 36, fontSize: 13 }

// Real-Time Searches deep link — same convention the app already uses
// from Reports/Alerts and History (see RealTimeHistory/@hooks/useFilters.js).
function realTimeSearchHref(searchId) {
	return `/real-time/searches?idSearch=${encodeURIComponent(searchId)}`
}

function todayISO() {
	return new Date().toISOString().slice(0, 10)
}

//: How far back the log opens on. Days, not hours: the engine runs on a
//: schedule and an operator opening this wants the last thing it did,
//: not a check on whether it has done anything since breakfast. It
//: defaulted to today while the screen was a mock that minted an event
//: every four seconds, so it was never empty; against real data on a
//: quiet Monday it opened on nothing and looked broken (Curro,
//: 2026-09-27).
const DEFAULT_WINDOW_DAYS = 7

function daysAgoISO(days) {
	const d = new Date()
	d.setDate(d.getDate() - days)
	return d.toISOString().slice(0, 10)
}

//: The cell said in words (CU-869fhtp3a): `31 Dec 2026` in a sentence,
//: `31 Dec` on a row — the calendar's own format, as `DateRangeField`.
const fmtDay   = (d) => formatDate(d, 'dd MMM yyyy')
const fmtShort = (d) => formatDate(d, 'dd MMM')

//: The window of the feed, for the empty state's sentence: nothing on
//: "All time", the default window by name, any other by its dates.
function windowPhrase(filters, defaultFrom, today) {
	const { from, to } = filters
	if (!from && !to) return ''
	if (from === defaultFrom && to === today) return ` in the last ${DEFAULT_WINDOW_DAYS} days`
	const a = fromIso(from), b = fromIso(to)
	if (a && b) return ` between ${fmtDay(a)} and ${fmtDay(b)}`
	if (a) return ` since ${fmtDay(a)}`
	return b ? ` until ${fmtDay(b)}` : ''
}

// Each event type in one of the app's soft tones: green done, blue held or derived, amber limited,
// red failed, grey no-op. A tone, not a colour per type: the label says which one it is.
const EVENT_META = {
	no_strategy:      { label: 'No strategy',       short: 'NO STRATEGY',
	                    icon: SearchOffIcon,  tone: 'neutral' },
	// Emitted since the first engine when no row of ours in the cell
	// resolves a rate-card face; never registered here, so it rendered as
	// a raw grey chip with no Reason block (UX, 2026-10-07; CU-869fd5fnu).
	no_rate_card:     { label: 'No rate card',      short: 'NO RATE CARD',
	                    icon: SearchOffIcon,  tone: 'neutral' },
	no_price_change:  { label: 'No price change',   short: 'NO CHANGE',
	                    icon: RemoveIcon,     tone: 'neutral' },
	capped:           { label: 'Capped by limit',   short: 'CAPPED',
	                    icon: CapIcon,        tone: 'warn' },
	quarantined:      { label: 'Quarantined',       short: 'QUARANTINED',
	                    icon: QuarantineIcon, tone: 'accent' },
	applied:          { label: 'Applied',           short: 'APPLIED',
	                    icon: CheckIcon,      tone: 'ok' },
	tethered:         { label: 'Tethered',          short: 'TETHERED',
	                    icon: LinkIcon,       tone: 'info' },
	pms_error:        { label: 'PMS error',         short: 'PMS ERROR',
	                    icon: ErrorIcon,      tone: 'err' },
	tethered_pms_error:{ label: 'Tethered error (system inconsistent)', short: 'TETHERED · SYSTEM INCONSISTENT',
	                    icon: BrokenIcon,     tone: 'err' },
	// Written by the flush, not by a decision (CU-869fb9wz6): the
	// pricelist did not hold what we last wrote, or two decisions
	// disagreed on one bracket and nothing was written.
	ladder_drift:     { label: 'Pricelist moved',   short: 'MOVED',
	                    icon: TuneIcon,       tone: 'warn' },
	ladder_conflict:  { label: 'Ladder conflict',   short: 'CONFLICT',
	                    icon: BrokenIcon,     tone: 'err' },
	setup_change:     { label: 'Setup change',      short: 'SETUP',
	                    icon: EditIcon,       tone: 'neutral' },
	// Legacy alias for rows written before the rename — mapped in
	// canonicalEventType() so filters/UI still see 'capped'.
	blocked_by_limit: { label: 'Capped by limit',   short: 'CAPPED',
	                    icon: CapIcon,        tone: 'warn' },
}

// "Empty" from the user's POV = today only. Cleared filters snap back
// to today, not to an all-time view — Curro's request, and it also
// keeps the streaming visible (new events land inside the window).
const EMPTY_FILTERS = () => ({
	search: '',
	from: daysAgoISO(DEFAULT_WINDOW_DAYS),
	to:   todayISO(),
	office: '*',
	sipp: '*',
	execution_id: '',
	// The cell — pickup date and duration (CU-869fhtp3a). Empty here, so
	// "Reset to today" and "Clear filters" clear it with everyone else.
	pickup_from: '',
	pickup_to: '',
	duration_from: null,
	duration_to: null,
	event_types: [],
	capped_by: [],
	blocked_by_floor: false,
	tether_vs_order: false,
	incomplete_market: false,
	decided_not_sent: false,
	outranked: false,
	// Off by default. The correlation between an edit and the
	// repricing that follows it is worth having, and worth asking for.
	include_setup: false,
})
// Truly unbounded reset — surfaced as "Clear all" in the filters bar.
const NO_FILTERS = {
	search: '', from: '', to: '', office: '*', sipp: '*', execution_id: '',
	pickup_from: '', pickup_to: '', duration_from: null, duration_to: null,
	event_types: [], capped_by: [], blocked_by_floor: false, tether_vs_order: false, incomplete_market: false,
	decided_not_sent: false, outranked: false,
	include_setup: false,
}

//: "Show the winning decision" from an outranked row (CU-869ffwj71). The
//: winner is usually on the same page — same pass, office and ACRISS, a
//: second apart — so it opens in place; when it is not, the feed narrows
//: to that pass · office · ACRISS and opens it once the page arrives.
const ActivityNavContext = createContext({ reveal: () => {} })

// The rate-card faces, by id, loaded once per page (CU-869fe6mf4): an
// event names its face by `payload.face_id` and nothing else, so the
// screen resolves the axes here and names them through the catalogue.
// Empty until loaded, or when the list could not be read — a face that
// cannot be resolved reads `—`, never an id.
const FacesContext = createContext({})

/** `'Sunny Cars · Prepaid · Zero excess'` of one event's rate card — a
 *  blank axis said as "All …" (CU-869fh646w) — or `null`. */
function useFaceLabel(row) {
	const facesById = useContext(FacesContext)
	const axes = useAxisCatalog()
	const sites = useSiteCatalog()
	return faceLabel(faceOf(row, facesById), axes, sites)
}

function FaceCell({ row }) {
	const label = useFaceLabel(row)
	return (
		<Typography sx={{ fontSize: 13, color: label ? dsColorNew.textTitle : dsColorNew.textMuted,
		                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
		            title={label || undefined}>
			{label || '—'}
		</Typography>
	)
}

/** The insurance of a competing offer arrives as a Price Seeker id and
 *  its catalogue lives in the axes, same as in Setup — so the page is
 *  wrapped once rather than every dialog fetching it (2026-10-03). */
export default function DsRepricingActivityPage() {
	return (
		<AxisCatalogProvider>
			{/* The site catalogue names the rate card of a decision by its
			    site, as Rate cards and Strategies do (CU-869fh646w, D1). */}
			<SiteCatalogProvider>
				<ActivityPage/>
			</SiteCatalogProvider>
		</AxisCatalogProvider>
	)
}

function ActivityPage() {
	const loc  = useLocation()
	const nav  = useNavigate()

	// Legacy /blocked redirects here with ?event_type=blocked_by_limit;
	// respect that on first mount but keep the today-only default.
	const [filters, setFilters] = useState(() => {
		const q = new URLSearchParams(loc.search)
		const et = q.get('event_type')
		// Opened from Executions on one pass: the pass may be older than
		// the default window, so the dates open up and the pass narrows.
		const execution = q.get('execution_id') || ''
		// Opened from a day on the Calendar: that day, nothing else.
		const from = q.get('from') || '', to = q.get('to') || ''
		return {
			...EMPTY_FILTERS(),
			...(execution ? { from: '', to: '', execution_id: execution } : {}),
			...(from || to ? { from, to } : {}),
			...(q.get('decided_not_sent') === '1' ? { decided_not_sent: true } : {}),
			// Opened from the Executions summary of a pass: the facet, not
			// an event type — an incomplete-market decision is an ordinary
			// `applied` and filtering by type would hide it.
			...(q.get('incomplete_market') === '1' ? { incomplete_market: true } : {}),
			// The two "shipped out of order" facets, seedable like the rest
			// (the floor one never was — CU-869ffwfb9).
			...(q.get('blocked_by_floor') === '1' ? { blocked_by_floor: true } : {}),
			...(q.get('tether_vs_order') === '1' ? { tether_vs_order: true } : {}),
			// The decisions another rate card outranked (CU-869ffwj71).
			...(q.get('outranked') === '1' ? { outranked: true } : {}),
			event_types: et ? [canonicalEventType(et)] : [],
			// The cell, from the brief's keys (`duration=7` or the pair);
			// a bad value reads as absent (CU-869fhtp3a, D5).
			...readCell(loc.search),
		}
	})
	const [page, setPage] = useState(1)
	const [expanded, setExpanded] = useState(() => new Set())
	const [live, setLive] = useState(true)

	// The cell is written back to the URL so a link to one day can be
	// shared (D5). Only these four keys: the rest of the bar still reads
	// the URL once and never writes it. Merged over the current search —
	// an `execution_id` that came in a link survives filtering by cell —
	// and `replace`, not push: Back must not undo filters one at a time.
	// On the first render `writeCell` gives back the same string the
	// state was seeded from, so nothing navigates.
	useEffect(() => {
		const next = writeCell(loc.search, filters)
		if (next !== loc.search) nav({ search: next }, { replace: true })
	}, [filters.pickup_from, filters.pickup_to, filters.duration_from, filters.duration_to])

	// Live is a poll now, not a fabrication: the feed refetches on a
	// timer while the tab is visible. The heartbeat that used to mint a
	// random event every four seconds is gone with the mock.

	function toggleRow(id) {
		setExpanded(prev => {
			const next = new Set(prev)
			next.has(id) ? next.delete(id) : next.add(id)
			return next
		})
	}

	// The server filters and paginates. It used to be an `Array.filter`
	// over everything the browser held, which worked while the log was
	// a mock and would search thirty fetched rows against a table of
	// tens of thousands.
	const feed = useActivityFeed({ filters, page, pageSize: PAGE_SIZE, live })
	const paged = feed.items
	const totalPages = Math.max(1, Math.ceil((feed.total || 0) / PAGE_SIZE))
	const currentPage = Math.min(page, totalPages)

	// Open one event by id — the winner of an outranked bracket, or a loser
	// from the winner's detail. On this page: open it. Elsewhere: narrow
	// the feed to the hint (pass, office, ACRISS) and open it on arrival.
	const pendingReveal = useRef(null)
	const openRow = (id) => {
		setExpanded(prev => new Set(prev).add(id))
		document.getElementById(`activity-row-${id}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
	}
	const reveal = useCallback((id, hint = {}) => {
		if (!id) return
		if (paged.some(r => r.id === id)) { openRow(id); return }
		pendingReveal.current = id
		setFilters(f => ({
			...EMPTY_FILTERS(),
			// The pass may be older than the default window; without a
			// pass, the current window stays and office · ACRISS narrow it.
			...(hint.execution_id ? { from: '', to: '', execution_id: hint.execution_id } : { from: f.from, to: f.to }),
			office: hint.office || '*',
			sipp: hint.sipp || '*',
		}))
		setPage(1)
	}, [paged])
	useEffect(() => {
		const id = pendingReveal.current
		if (!id || feed.loading) return
		if (paged.some(r => r.id === id)) { pendingReveal.current = null; openRow(id) }
	}, [paged, feed.loading])

	// Counters do NOT follow the filters, on purpose: they say what
	// exists, and a number that moves when you filter cannot help you
	// decide what to filter by. The window and the cell are the
	// exceptions (D6): they are the question, not a facet of the answer.
	const { counts: rawCounts, capCounts, blockedByFloor, tetherVsOrder, incompleteMarket,
	        quarantinedPending, outranked, loaded: countsLoaded } = useActivityCounts({ from: filters.from, cell: cellOf(filters), live })
	const [modeState] = useRepricingMode()
	const toast = useToast()
	const [confirmResolve, resolveDialog, updateResolveDialog] = useConfirm()

	// The faces, once. Not refetched with the feed: a face is Setup data
	// and the list is small; a deleted face reads `—` until reload.
	const [facesById, setFacesById] = useState({})
	useEffect(() => {
		let alive = true
		setupApi.faces.list()
			.then(faces => { if (alive) setFacesById(indexFaces(faces)) })
			.catch(() => { if (alive) setFacesById({}) })
		return () => { alive = false }
	}, [])

	// Approve pushes the price computed when the decision was held — hours
	// old by now — and discard leaves the PMS as it is; both are said
	// before the click. The engine answers 409 with a code when it will
	// not (simulation, already resolved), and that is shown as such.
	const resolveEvent = async (row, action) => {
		if (action === 'override') return overrideEvent(row)
		const ok = await confirmResolve(action === 'approve'
			? { title: `Approve and push ${eur(row.new_price)}/day?`,
			    body: 'This is the price computed when the decision was held, not a fresh one. It goes to every pricelist the rate card points at, and its tethered children with it.',
			    confirmLabel: 'Approve and push' }
			: { title: 'Discard this decision?',
			    body: 'The PMS keeps its current price. The next run may propose the same change again.',
			    confirmLabel: 'Discard' })
		if (!ok) return
		try {
			const r = await activityActions[action](row.id)
			toast.success(action === 'approve'
				? `Pushed ${r.pushed_price} €/day in ${r.writes} write${r.writes === 1 ? '' : 's'}${r.children ? `, ${r.children} tethered` : ''}.`
				: 'Discarded.')
			feed.reload()
		} catch (e) {
			// `ApiError` carries the server's `code` and its `error` as message.
			const code = e?.code
			toast.error(code === 'simulation_mode' ? 'Approve is refused in simulation: nothing is sent.'
			          : code === 'already_resolved' ? 'Somebody already resolved this one.'
			          : (e?.message || 'Could not resolve the event'))
			feed.reload()
		}
	}
	// Override: the dialog opens at once and the engine's preview fills it
	// in — the amount that would go out, the caps it jumps, the consistency
	// adjustment — before the operator can confirm (UX, CU-869fdg4nu).
	const overrideEvent = async (row) => {
		const ov = overridable(row.cap_chain)
		const pending = confirmResolve({
			title: 'Override this cap?',
			body: 'Loading what would go out…',
			detail: `Bypasses: ${bypassesSentence(ov.bypasses)}`,
			input: { label: 'Reason', placeholder: 'Why this price, for this one hit', required: true },
			confirmLabel: 'Override and push', disabled: true,
		})
		try {
			const preview = await activityActions.overridePreview(row.id)
			const nothing = !!preview?.nothing_to_push
			const adj = preview?.adjusted_by_consistency
			updateResolveDialog({
				title: nothing ? 'Nothing to push' : `Override and push ${eur(preview.price)}/day?`,
				body: nothing
					? `The override lands on the price the PMS already holds (${eur(preview.pushed_now)}/day).`
					: `The PMS holds ${eur(preview.pushed_now)}/day, the capped price. This pushes the price before the cap (${eur(preview.proposed_pvp)} shelf), converted by the markup this pass used. One hit: the next pass caps again.`,
				detail: (
					<>
						Bypasses: {bypassesSentence(preview.bypasses?.length
							? preview.bypasses.map(b => ({ kind: b.kind, label: b.label })) : ov.bypasses)}
						{adj && adj.direction !== 'empty' && (
							<><br/>Kept in order with <strong>{adj.group_name}</strong>: {eur(preview.price)}/day
							instead of {eur(adj.from)} — would put it {adj.direction === 'raised' ? 'under' : 'over'}{' '}
							{adj.reference_acriss} ({eur(adj.reference_price)}/day).</>
						)}
					</>
				),
				disabled: nothing,
			})
		} catch (e) {
			updateResolveDialog({ title: 'Nothing to push', body: e?.message || 'The engine could not preview this override.',
			                      disabled: true })
		}
		const reason = await pending
		if (!reason) return
		try {
			const r = await activityActions.override(row.id, reason)
			toast.success(`Pushed ${r.pushed_price} €/day in ${r.writes} write${r.writes === 1 ? '' : 's'}${r.children ? `, ${r.children} tethered` : ''}.`)
			feed.reload()
		} catch (e) {
			const code = e?.code
			toast.error(code === 'simulation_mode' ? 'Override is refused in simulation: nothing is sent.'
			          : code === 'already_resolved' ? 'Somebody already resolved this one.'
			          : code === 'nothing_to_push' ? 'Nothing to push: the PMS already holds that price.'
			          : code === 'nothing_to_override' ? 'Nothing to override here.'
			          : code === 'not_capped' ? 'Only a capped decision can be overridden.'
			          : code === 'pms_error' ? `The PMS refused the write; the row stays pending. ${e?.message || ''}`
			          : (e?.message || 'Could not override the event'))
			feed.reload()
		}
	}
	const counters = useMemo(() => {
		const c = { total: 0 }
		for (const t of ACTIVITY_EVENT_TYPES) c[t] = 0
		for (const [type, n] of Object.entries(rawCounts)) {
			const key = canonicalEventType(type)
			c[key] = (c[key] || 0) + n
			// Setup edits share the feed but not this counter: it sits
			// beside Applied, Capped and Errors, so it reads as "what
			// the engine did" and an edit is not something it did.
			if (key !== 'setup_change') c.total += n
		}
		// Pending is a server number: held decisions minus the ones that
		// have a resolution row. The page cannot know it on its own.
		c.quarantined_pending = quarantinedPending
		return c
	}, [rawCounts, quarantinedPending])

	// The default window is NOT counted as "active" — it is the
	// baseline. Only user-added narrowings raise the counter. One
	// predicate per filter, keyed by its filter key (a range is one
	// filter, under its first key), so that the two counts below come
	// from the same list (CU-869fhtp3a, C3).
	const today = todayISO()
	const defaultFrom = daysAgoISO(DEFAULT_WINDOW_DAYS)
	const activeBy = {
		search:            filters.search !== '',
		from:              filters.from !== '' && filters.from !== defaultFrom,
		to:                filters.to   !== '' && filters.to   !== today,
		office:            filters.office !== '*',
		sipp:              filters.sipp !== '*',
		execution_id:      !!filters.execution_id,
		pickup_from:       !!(filters.pickup_from || filters.pickup_to),
		duration_from:     filters.duration_from != null || filters.duration_to != null,
		event_types:       filters.event_types.length > 0,
		capped_by:         filters.capped_by.length > 0,
		blocked_by_floor:  !!filters.blocked_by_floor,
		tether_vs_order:   !!filters.tether_vs_order,
		incomplete_market: !!filters.incomplete_market,
		decided_not_sent:  !!filters.decided_not_sent,
		outranked:         !!filters.outranked,
	}
	const activeFiltersCount = Object.values(activeBy).filter(Boolean).length
	// The filters the counters do NOT follow — what makes "N of M shown"
	// true. With only the window and the cell on, M would equal N (D6).
	const narrowingCount = Object.keys(activeBy)
		.filter(k => activeBy[k] && !windowKeys.includes(k)).length

	return (
		/* The only DS Repricing screen that was not wrapped, so it was
		   the only one without the page title and breadcrumb the others
		   have (Curro, 2026-09-28). The card keeps its own heading: the
		   wrapper names the page, the card names what is in it. */
		<PageWrapper
			title={DS_REPRICING_ROUTES.ACTIVITY_TITLE}
			subtitle={DS_REPRICING_ROUTES.ACTIVITY_SUBTITLE}
		>
		{/* The page's white band, as every analysis page of the app: the filters, the Live / Paused
		    switch on the right, and the facets as the app's filter chips. Every change still applies at
		    once. */}
		<FiltersBar filters={filters} onChange={(next) => { setFilters(next); setPage(1) }}
		            activeCount={activeFiltersCount} narrowing={narrowingCount}
		            total={counters.total} filteredTotal={feed.total}
		            capCounts={capCounts} counts={rawCounts}
		            // No numbers on the facets until the counts arrive: a "0" must never mean "unknown"
		            loaded={countsLoaded}
		            blockedByFloor={countsLoaded ? blockedByFloor : null}
		            tetherVsOrder={countsLoaded ? tetherVsOrder : null}
		            incompleteMarket={countsLoaded ? incompleteMarket : null}
		            outranked={countsLoaded ? outranked : null}
		            live={live} onLive={setLive}/>
		{/* Which mode produced this log. A simulation read as real pushes
		    is the costliest misreading this screen can cause. */}
		<ModeBanner/>
		{resolveDialog}
		<ActivityNavContext.Provider value={{ reveal }}>
		<FacesContext.Provider value={facesById}>
		{/* The counters as the app's figure cards (My Business, Main Page). "Inconsistent" and
		    "Floor vs order" turn red and pulse when they are not zero: nothing else on this screen
		    says it. */}
		{!countsLoaded ? <Box sx={{ mb: 3 }}><MbKpiSkeleton count={8} min={220}/></Box> : (
		<MbKpiRow sx={{ mb: 3, gridTemplateColumns: 'repeat(4, minmax(0, 1fr))' }}>
			<MbKpiCard icon="list"                 label="Total"      value={counters.total}/>
			<MbKpiCard icon="circle-check"         label="Applied"    value={counters.applied} color={dsColorNew.successText}/>
			<MbKpiCard icon="link"                 label="Tethered"   value={counters.tethered} color={dsColorNew.cyan}/>
			<MbKpiCard icon="arrow-down-to-line"   label="Capped"     value={counters.capped} color={dsColorNew.warningBar}/>
			<MbKpiCard icon="flask"                label="Quarantine" value={counters.quarantined_pending} color={dsColorNew.appBlue}/>
			<MbKpiCard icon="circle-exclamation"   label="Errors"     value={counters.pms_error} color={dsColorNew.dangerRed}/>
			<MbKpiCard icon="link-slash"           label="Inconsistent" value={counters.tethered_broken_pending}
			           color={dsColorNew.dangerRed} sx={criticalSx(counters.tethered_broken_pending > 0)}/>
			{/* A price still went out — what failed is the category order, because a floor would
			    not yield. Its own counter, loud, because nothing else on this screen says it. */}
			<MbKpiCard icon="triangle-exclamation" label="Floor vs order" value={blockedByFloor}
			           color={dsColorNew.dangerRed} sx={criticalSx(blockedByFloor > 0)}/>
		</MbKpiRow>
		)}
		<Surface padded={false} sx={setupScopeSx}>
			<Box sx={{ p: 2.5 }}>
				<SurfaceHeader
					// The card's notes, with its description, behind the ⓘ of its title
					info={<>
						<Box>
						<strong>Event vocabulary:</strong>{' '}
						<strong>Applied</strong> — push landed in the PMS.{' '}
						<strong>Tethered</strong> — child push derived from a parent
						applied via a tethering rule (parent price × spread). Each
						tethered event links back to the parent it inherited from.{' '}
						<strong>Capped</strong> — push landed, but at the limit value
						instead of the proposed price (override to push the original
						price for this one hit).{' '}
						<strong>Quarantined</strong> — strategy is in dry-run mode; the
						engine computed but did not push (apply manually or discard).{' '}
						<strong>No strategy / No change</strong> — traceable no-ops.{' '}
						<strong>PMS error</strong> — the push failed at the API layer
						(retry or acknowledge).{' '}
						<strong>Tethered error</strong> — the child push failed while
						the parent succeeded, leaving parent and child out of sync in
						the PMS. Highlighted in red and pulses in the KPI: fix ASAP,
						either by retrying the push or reconciling by hand.
						</Box>
					</>}
					icon="list-check"
					title="Events"
					subtitle={<>Every hit the repricing engine handles — successful pushes,
					no-op evaluations, safety-net blocks and PMS errors. Filter by date,
					office or event type. Expand any row for the full trace (strategy
					resolution, deltas per rate card, request/response JSON).</>}
					actions={
						!(feed.loading && paged.length === 0) && (
							<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
								Newest first · shows {paged.length} of {feed.total}
							</Typography>
						)
					}
					sx={{ mb: 2 }}
				/>

				{/* A failed request used to look exactly like an empty log:
				    the feed swallowed the error into an empty list and the
				    screen drew "no events". That is how a 404 on the wrong
				    base URL passed for "the engine has not run"
				    (2026-09-27). An error is now the first thing said. */}
				{/* Said once at the top as well as per row: an operator
				    scanning for a price change should not have to notice a
				    three-letter suffix to learn that nothing was published. */}
				{!feed.error && paged.some(r => r.simulated) && (
					<AppNote sx={{ mb: 2 }}>
						<strong>Simulation.</strong> This tenant is in dry run: the
						engine decided these prices and none of them was sent to the
						PMS. Rows produced this way are marked <strong>SIM</strong>.
					</AppNote>
				)}

				{feed.error && (
					<AppNote severity="error" sx={{ mb: 2 }}>
						Could not load the activity log: {feed.error}{' '}
						<Button variant="text" size="small" onClick={() => feed.reload()}
						        sx={{ fontWeight: 600, minWidth: 0, p: 0, ml: 0.5, verticalAlign: 'baseline' }}>
							Retry
						</Button>
					</AppNote>
				)}

				{/* The first page on its way is not an empty log: the table's skeleton until it lands
				    (an empty state that turns into rows a few seconds later said the opposite of the truth). */}
				{feed.error ? null : feed.loading && paged.length === 0 ? (
					<AppTableSkeleton rows={8} columns={8}/>
				) : paged.length === 0 ? (
					/* Three different silences, and telling them apart is the
					   whole point: nothing in this window, nothing matching
					   these filters, or nothing at all. The first is the
					   common one and used to read as the third. */
					<EmptyState
						icon={<SearchOffIcon/>}
						title={
							activeFiltersCount > 0 ? 'No events match these filters'
							: counters.total === 0 ? 'No events yet'
							: `Nothing in the last ${DEFAULT_WINDOW_DAYS} days`}
						description={
							activeFiltersCount > 0
							  ? (cellSentence(filters, fmtDay)
							      ? `Nothing for ${cellSentence(filters, fmtDay)}${windowPhrase(filters, defaultFrom, today)}. `
							        + 'The engine may not have priced this cell yet — widen the dates, '
							        + 'or clear the pickup and duration filters.'
							      : 'Loosen the filters above.')
							: counters.total === 0
							  ? 'When the engine starts processing rate shops, every step will land here.'
							  : `The engine has recorded ${counters.total} events, none of them in this window. Widen the dates to see them.`}
						action={
							activeFiltersCount > 0
							  ? <Stack direction="row" gap={1} justifyContent="center">
							      <Button variant="outlined"
							              onClick={() => setFilters(EMPTY_FILTERS())}
							              sx={detailButtonSx}>
							        Clear filters
							      </Button>
							      {/* The window off, the cell kept: "has this cell ever
							          been priced?" is the question that follows (D7). */}
							      {(filters.from || filters.to) && (
							        <AppBulkEditAction onClick={() => setFilters(f => ({ ...f, from: '', to: '' }))}>
							          Show everything
							        </AppBulkEditAction>
							      )}
							    </Stack>
							: counters.total === 0 ? null
							  : <AppBulkEditAction
							            onClick={() => setFilters(f => ({ ...f, from: '', to: '' }))}>
							      Show everything
							    </AppBulkEditAction>}
					/>
				) : (
					<>
						{/* While a page is on its way the table holds the
						    previous one, which is worse than holding nothing:
						    the pager already moved, so for a second or two the
						    screen says "page 3" over the rows of page 2
						    (Curro, 2026-09-28). It is dimmed and sealed off
						    instead, with a bar to say something is happening.
						    Height is kept so the page does not jump, and the
						    live poll refetches quietly, so this never flashes
						    on its own. */}
						<Box sx={{ position: 'relative' }}>
						{feed.loading && (
							<>
								<LinearProgress sx={{
									position: 'absolute', top: 0, left: 0, right: 0,
									zIndex: 2, borderTopLeftRadius: 4, borderTopRightRadius: 4,
								}}/>
								<Box sx={{ position: 'absolute', inset: 0, zIndex: 1,
								           bgcolor: alpha(dsColorNew.white, 0.6) }}/>
							</>
						)}
						<TableContainer sx={{ overflow: 'auto',
						                      opacity: feed.loading ? 0.45 : 1,
						                      pointerEvents: feed.loading ? 'none' : 'auto',
						                      transition: 'opacity 120ms ease' }}>
							{/* Body at the app tables' 13px; a fixed layout so the ten columns share the card's width
							    instead of pushing Δ Price and Resolution off its edge. */}
							<Table size="small" sx={{ tableLayout: 'fixed', '& td': { fontSize: 13 }, '& td code': { fontSize: 13 } }}>
								<TableHead>
									<TableRow>
										<HeadCell sx={{ width: 44 }}></HeadCell>
										<HeadCell sx={{ width: 104 }}>Time</HeadCell>
										<HeadCell sx={{ width: '14%' }}>Event</HeadCell>
										<HeadCell sx={{ width: '7%' }}>Office</HeadCell>
										<HeadCell sx={{ width: '8%' }}>ACRISS</HeadCell>
										{/* Which of our faces the decision was for. Two
										    `no_strategy` rows of one cell are identical to
										    the pixel without it (CU-869fe6mf4). One line,
										    no colour: in a log the event chip is the one
										    that must stand out. */}
										<HeadCell sx={{ width: '10%' }}>Face</HeadCell>
										<HeadCell sx={{ width: '13%' }}>Trigger</HeadCell>
										<HeadCell>Strategy</HeadCell>
										<HeadCell align="right" sx={{ width: '11%' }}>Δ Price</HeadCell>
										<HeadCell sx={{ width: '13%' }}>Resolution</HeadCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{paged.map((r, i) => {
										const isOpen = expanded.has(r.id)
										// The shared `rowHoverSx` stripes with
										// `nth-of-type(even)`, which cannot work here:
										// every event is two `<tr>`s, so the wash landed
										// on the collapsed detail row and the table read
										// as having no zebra at all. Striping on the map
										// index puts it back on the events, and the
										// detail row takes its parent's band so an open
										// event still reads as one block.
										const band = i % 2 ? dsColorNew.tableRowAlt : dsColorNew.white
										return (
											<React.Fragment key={r.id}>
												<TableRow id={`activity-row-${r.id}`}
												          sx={{ bgcolor: isOpen ? dsColorNew.pillActiveBg : band,
												                cursor: 'pointer',
												                transition: 'background-color 120ms ease',
												                '&:hover': { bgcolor: dsColorNew.pillActiveBg },
												                '& td': isOpen
												                  ? { borderBottom: 'none', fontWeight: 600 }
												                  : undefined }}
												          onClick={() => toggleRow(r.id)}>
													<TableCell sx={cellPadSx}>
														<IconButton size="small"
														            onClick={(e) => { e.stopPropagation(); toggleRow(r.id) }}
														            sx={{ color: dsColorNew.textMuted }}>
															{isOpen
																? <ExpandMoreIcon fontSize="small"/>
																: <ChevronRightIcon fontSize="small"/>}
														</IconButton>
													</TableCell>
													<TableCell sx={cellPadSx}>
														<TimeStamp ts={r.ts}/>
													</TableCell>
													<TableCell sx={cellPadSx}>
														<EventChip type={r.event_type} simulated={r.simulated}/>
														{/* Which limit bound it, on the row itself. Both
														    kinds of cap produce a `capped` event — they
														    are one decision, and a chain can hold both —
														    so the distinction lives here and in the
														    filters rather than in a second event type. */}
														{r.capped_by?.length > 0 && (
															<Typography sx={{ fontSize: 12, mt: 0.4,
															                  color: dsColorNew.textMuted }}>
																{r.capped_by.map(k => CAP_LABELS[k] || k).join(' + ')}
															</Typography>
														)}
														{/* A tethered child that kept its spread and left its
														    group out of order says so here, in the same slot —
														    the floor case never said it on the row, and that is
														    the gap this one must not repeat (CU-869ffwfb9). */}
														{spreadRowLabel(r) && (
															<Typography sx={{ fontSize: 12, mt: 0.4, color: dsColorNew.textMuted }}>
																{spreadRowLabel(r)}
															</Typography>
														)}
														{/* Our own row has no kind recorded on an axis that
														    still filters (CU-869fh646w): the reason is in our
														    row, not in the market, and the operator has to
														    see it without opening. */}
														{ownKindRowLabel(r.payload?.reason) && (
															<Typography sx={{ fontSize: 12, mt: 0.4, color: dsColorNew.textMuted }}>
																{ownKindRowLabel(r.payload?.reason)}
															</Typography>
														)}
													</TableCell>
													<TableCell sx={cellPadSx}>
														<Box component="code" sx={{ fontFamily: 'inherit',
														                            fontSize: 13, fontWeight: 700 }}>
															{r.scope?.office || '—'}
														</Box>
													</TableCell>
													<TableCell sx={cellPadSx}>
														<Box component="code" sx={{ fontFamily: 'inherit',
														                            fontSize: 13, fontWeight: 700 }}>
															{r.scope?.sipp || '—'}
														</Box>
														{/* The other two coordinates of the cell, always (D8,
														    CU-869fhtp3a): with a pickup range on, rows are
														    otherwise identical until opened. Empty on a setup
														    change, which has no scope. */}
														{cellRowLabel(r.scope, fmtShort) && (
															<Typography sx={{ fontSize: 12, mt: 0.4, color: dsColorNew.textMuted,
															                  whiteSpace: 'nowrap' }}>
																{cellRowLabel(r.scope, fmtShort)}
															</Typography>
														)}
													</TableCell>
													<TableCell sx={cellPadSx}>
														<FaceCell row={r}/>
													</TableCell>
													<TableCell sx={cellPadSx}>
														<Stack direction="row" gap={0.5} alignItems="center">
															<Typography sx={{ fontSize: 13,
															                  color: dsColorNew.textMuted }}>
																{r.scope?.source || '—'}
															</Typography>
															{r.scope?.search_id && (
																<IconButton component="a"
																            href={realTimeSearchHref(r.scope.search_id)}
																            target="_blank"
																            rel="noopener noreferrer"
																            title="View in Real Time Searches"
																            size="small"
																            onClick={(e) => e.stopPropagation()}
																            sx={{ color: dsColorNew.appBlue,
																                  p: 0.25,
																                  '&:hover': { bgcolor: dsColorNew.pillActiveBg } }}>
																	<OpenSearchIcon sx={{ fontSize: 18 }}/>
																</IconButton>
															)}
														</Stack>
													</TableCell>
													<TableCell sx={cellPadSx}>
														{/* A setup edit has no strategy; what it has is the
														    thing that was edited, which is what this column
														    is for on those rows. */}
														<Typography sx={{ fontSize: 13,
														                  overflow: 'hidden',
														                  textOverflow: 'ellipsis',
														                  whiteSpace: 'nowrap' }}
														            title={r.setup
														                     ? `${r.setup.entity_label} · ${r.setup.name || r.setup.entity_id || ''}`
														                     : (r.strategy_name || '—')}>
															{r.setup
																? <>
																    <strong>{r.setup.entity_label}</strong>
																    {r.setup.name ? ` · ${r.setup.name}` : ''}
																  </>
																: r.strategy_name
																  || <em style={{ color: dsColorNew.textMuted }}>none</em>}
														</Typography>
													</TableCell>
													<TableCell align="right" sx={cellPadSx}>
														<DeltaCell row={r}/>
													</TableCell>
													<TableCell sx={cellPadSx}
													           onClick={(e) => e.stopPropagation()}>
														<ResolutionCell row={r} onResolve={resolveEvent} liveMode={modeState.mode === 'live'}/>
													</TableCell>
												</TableRow>
												{/* The detail has to look like a different
												    thing, not like another row. `grey.100`
												    against a `grey.50` zebra is a shade apart
												    and the eye could not find the seam: where
												    a row ended and its contents began was a
												    guess (Curro, 2026-09-28).
												    A cool tint instead of another neutral, a
												    rule above and below, and an accent down
												    the left so the block reads as hanging off
												    its row. */}
												<TableRow sx={{ bgcolor: band }}>
													<TableCell colSpan={10}
													           sx={{ p: 0, border: 'none',
													                 borderBottom: isOpen ? '2px solid' : 'none',
													                 borderBottomColor: dsColorNew.noteInfoBorder }}>
														<Collapse in={isOpen} unmountOnExit>
															<Box sx={{ bgcolor: dsColorNew.contentBg, p: 2,
															           borderTop: '2px solid',
															           borderTopColor: dsColorNew.noteInfoBorder,
															           borderLeft: '4px solid',
															           borderLeftColor: dsColorNew.appBlue }}>
																<EventDetail row={r}/>
															</Box>
														</Collapse>
													</TableCell>
												</TableRow>
											</React.Fragment>
										)
									})}
								</TableBody>
							</Table>
						</TableContainer>
						</Box>

						{/* The app tables' pager: "1-30 of 1261" and the arrows, 30 a page. */}
						{feed.total > PAGE_SIZE && (
							<TablePagination component="div" count={feed.total}
							                 page={currentPage - 1} rowsPerPage={PAGE_SIZE}
							                 rowsPerPageOptions={[PAGE_SIZE]}
							                 onPageChange={(_, p) => setPage(p + 1)}/>
						)}
					</>
				)}

			</Box>

		</Surface>
		</FacesContext.Provider>
		</ActivityNavContext.Provider>
		</PageWrapper>
	)
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

//: The caps an operator asks about. The policy-stage guards exist too,
//: but they fire rarely and would put four chips on the bar to split a
//: number that is nearly all these two.
const CAP_FILTERS = [
	{ key: 'markup_cap',      label: 'Markup limit' },
	{ key: 'price_limit',     label: 'Price limit' },
	// Keyed by `source.kind` on the server as here (CU-869fe8jrk):
	// "Price limit" is an ordinary limit, this one a protection.
	{ key: 'date_protection', label: 'Date protection' },
	{ key: 'category_consistency', label: 'Category consistency' },
]

function FiltersBar({ filters, onChange, activeCount, narrowing = activeCount, total, filteredTotal,
                     capCounts = {}, counts = {}, blockedByFloor, tetherVsOrder, incompleteMarket,
                     outranked, live, onLive, loaded = true }) {
	const setF = (patch) => onChange({ ...filters, ...patch })
	// The tenant's own offices. The mock listed six Canarian airports
	// this tenant does not have, so the filter could only ever return
	// nothing (Curro, 2026-09-28).
	const { offices } = useOfficeScope()
	const { tree } = useCategories()
	const [sippOptions, sippGroups] = useMemo(() => {
		const groups = {}
		for (const m of tree || []) for (const code of m.sipps || []) groups[code] = m.name
		return [Object.keys(groups).sort(), groups]
	}, [tree])
	// A facet with its number. No number until the server gives one — a "0" must never mean "unknown".
	const facet = (on, label, n, toggle, title) => (
		<AppFilterChip selected={!!on} label={n == null ? label : `${label} · ${n}`}
		               title={title} onClick={toggle}/>
	)
	return (
		<AppFilterPanel
			togglesRight={
				<AppPillToggle size="small" value={live ? 'live' : 'paused'}
				               onChange={(v) => onLive(v === 'live')}
				               options={[{ value: 'live', label: 'LIVE' }, { value: 'paused', label: 'Paused' }]}/>
			}
			toggles={
				<TextField size="small"
				           placeholder="Search by office, ACRISS, strategy, source, pricelist…"
				           value={filters.search}
				           onChange={(e) => setF({ search: e.target.value })}
				           InputProps={{
				               startAdornment: (
				                   <SearchIcon sx={{ fontSize: 14, color: dsColorNew.textSubtle, mr: 1 }}/>
				               ),
				           }}
				           sx={{ width: 520, maxWidth: 1 }}/>
			}
			actions={<>
				{/* "N of M shown" only when a filter the counters do not follow
				    is on; with just the window and the cell, M would be N (D6). */}
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mr: 'auto' }}>
					{!loaded ? '' : narrowing > 0
					  ? `${filteredTotal} of ${total} shown`
					  : `${total} event${total === 1 ? '' : 's'}`}
				</Typography>
				{activeCount > 0 && (
					<Button variant="outlined" onClick={() => onChange(EMPTY_FILTERS())} sx={secondaryButtonSx}>
						Reset to today
					</Button>
				)}
				<Button variant="outlined" onClick={() => onChange(NO_FILTERS)} sx={secondaryButtonSx}>
					All time
				</Button>
			</>}>
			<Box sx={{ ...filterPanelGridSx(4),
			           '& .MuiAutocomplete-root, & .MuiTextField-root, & .MuiFormControl-root': { width: 1, minWidth: '0 !important' } }}>
				<DateRangeField from={filters.from} to={filters.to}
				                label="Dates" placeholder={`Last ${DEFAULT_WINDOW_DAYS} days`}
				                cleanable presets={pastPresets()} width="100%"
				                onChange={({ from, to }) => setF({ from: from || '', to: to || '' })}/>

				<TextField select size="small" label="Office" value={filters.office}
				           onChange={(e) => setF({ office: e.target.value })}>
					<MenuItem value="*"><em>Any office</em></MenuItem>
					{offices.map(o => (
						<MenuItem key={o.code} value={o.code}>
							<Box component="span" sx={{ mr: 1, fontWeight: 600 }}>{o.code}</Box>
							{o.name !== o.code ? o.name : ''}
						</MenuItem>
					))}
				</TextField>

				{/* One ACRISS at a time, from the PMS catalogue; a code the
				    catalogue does not know can still be typed — the log may
				    hold one the market returned (Curro, 2026-10-03). */}
				<Autocomplete size="small" freeSolo autoHighlight
				              options={sippOptions}
				              groupBy={(o) => sippGroups[o] || ''}
				              value={filters.sipp === '*' ? null : filters.sipp}
				              onChange={(_, v) => setF({ sipp: (v || '').trim().toUpperCase() || '*' })}
				              onInputChange={(_, v, reason) => {
				                  if (reason === 'clear') setF({ sipp: '*' })
				              }}
				              renderInput={(params) => (
				                  <TextField {...params} label="ACRISS" placeholder="Any ACRISS"
				                             InputLabelProps={{ shrink: true }}/>
				              )}/>

				{/* The other two coordinates of a cell, after Office and ACRISS
				    in the order the detail's Scope block lists them (CU-869fhtp3a,
				    D1). Pickup is the same calendar as Dates, looking forward: a
				    day is click, click, OK on one square — or Today / Tomorrow in
				    one click — and reads as one day. A one-sided value can only
				    arrive by URL; it shows as a hint and is sent open on that side. */}
				<DateRangeField from={filters.pickup_from} to={filters.pickup_to}
				                label="Pickup" placeholder="Any pickup date"
				                cleanable collapseSameDay presets={pickupPresets()} width="100%"
				                onChange={({ from, to }) => setF({ pickup_from: from || '', pickup_to: to || '' })}/>

				{/* Duration: one value or a range, never "7 and longer" — the
				    empty second box mirrors the first (D3). Commits on blur or
				    Enter, not per keystroke: a "1" on its way to "14" is not a
				    request. Outside 1–180 the box goes red and nothing is sent. */}
				<DurationRangeField label="Duration" commitOn="blur" min={DURATION_MIN} max={DURATION_MAX}
				                    value={filters.duration_from == null && filters.duration_to == null ? null
				                           : { min: filters.duration_from, max: filters.duration_to }}
				                    onChange={(v) => setF({ duration_from: v?.min ?? null, duration_to: v?.max ?? null })}/>

				<Autocomplete multiple size="small"
				              options={ACTIVITY_EVENT_TYPES}
				              value={filters.event_types}
				              onChange={(_, v) => setF({ event_types: v })}
				              getOptionLabel={(t) => EVENT_META[t]?.label || t}
				              renderTags={(list, getTagProps) =>
				                list.map((t, idx) => (
				                  <EventChip {...getTagProps({ index: idx })}
				                             key={t} type={t}/>
				                ))}
				              renderOption={(props, t) => (
				                <li {...props} key={t}>
				                  <EventChip type={t}/>
				                </li>
				              )}
				              renderInput={(params) => (
				                <TextField {...params} label="Event" InputLabelProps={{ shrink: true }}
				                           placeholder={filters.event_types.length ? '' : 'Any event type'}/>
				              )}/>
			</Box>

			{/* The facets: the app's filter chips, soft blue while on. Each one narrows the log; the
			    cap ones narrow `capped` events without selecting the event type, because a decision
			    can carry two caps at once. */}
			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, alignItems: 'center' }}>
				<AppFilterChip icon="gear" selected={!!filters.include_setup}
				               label={`Setup changes${loaded && counts?.setup_change ? ` · ${counts.setup_change}` : ''}`}
				               onClick={() => setF({ include_setup: !filters.include_setup })}/>
				{facet(filters.tether_vs_order, 'Spread vs order', loaded ? (tetherVsOrder ?? 0) : null,
				       () => setF({ tether_vs_order: !filters.tether_vs_order }))}
				{facet(filters.blocked_by_floor, 'Floor vs order', blockedByFloor,
				       () => setF({ blocked_by_floor: !filters.blocked_by_floor }))}
				{facet(filters.incomplete_market, 'Incomplete market', incompleteMarket,
				       () => setF({ incomplete_market: !filters.incomplete_market }),
				       'Nobody held the position the strategy asked for, so the engine priced above the dearest competitor instead of refusing.')}
				{facet(filters.decided_not_sent, 'Decided, not sent', null,
				       () => setF({ decided_not_sent: !filters.decided_not_sent }))}
				{facet(filters.outranked, 'Outranked', outranked,
				       () => setF({ outranked: !filters.outranked }),
				       "Decided, never sent: another rate card's strategy outranked it on the same bracket, or won the tie by moving the price more.")}
				{CAP_FILTERS.map(({ key, label }) => {
					const on = filters.capped_by.includes(key)
					return <React.Fragment key={key}>
						{facet(on, label, capCounts[key], () => setF({
							capped_by: on
							  ? filters.capped_by.filter(k => k !== key)
							  : [...filters.capped_by, key],
						}))}
					</React.Fragment>
				})}
				{filters.execution_id && (
					<AppFilterChip selected icon="xmark"
					               label={`Pass ${filters.execution_id.replace(/^rp-/, '').slice(0, 13)}…`}
					               title={`Only the decisions of pass ${filters.execution_id}. Remove to see everything.`}
					               onClick={() => setF({ execution_id: '', from: daysAgoISO(DEFAULT_WINDOW_DAYS), to: todayISO() })}/>
				)}
			</Box>
		</AppFilterPanel>
	)
}

// ---------------------------------------------------------------------------
// Cells
// ---------------------------------------------------------------------------
function TimeStamp({ ts }) {
	const d = new Date(ts)
	const date = displayDay(d.toISOString())
	const time = d.toISOString().slice(11, 16)
	return (
		<Box>
			<Typography sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.3, whiteSpace: 'nowrap' }}>
				{date}
			</Typography>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				{time} UTC
			</Typography>
		</Box>
	)
}

// The event words in sentence case, as the app's other chips; the acronyms stay as they are
const ACRONYMS = new Set(['PMS', 'SIM', 'SIPP', 'ACRISS', 'API'])
const sentenceCase = (text) => String(text).split(' ')
	.map((w, i) => ACRONYMS.has(w) ? w : (i === 0 ? w.charAt(0) + w.slice(1).toLowerCase() : w.toLowerCase()))
	.join(' ')

function EventChip({ type, simulated = false, ...rest }) {
	const meta = EVENT_META[type] || { label: type, tone: 'neutral' }
	const Icon = meta.icon
	// A decision made in dry run never reached the PMS, and "APPLIED"
	// on its own claims it did. The word changes rather than the event
	// type, so the counters stay comparable across a switch to live
	// (Curro, 2026-09-28).
	const label = sentenceCase(simulated
		? `${meta.short || meta.label} · SIM`
		: (meta.short || meta.label))
	return (
		<Chip {...rest} size="small"
		      title={simulated
		          ? 'Simulation: the engine decided this and the tenant is in dry run, so nothing was sent to the PMS.'
		          : undefined}
		      label={label}
		      icon={Icon ? <Icon/> : undefined}
		      sx={toneChipSx(meta.tone)}/>
	)
}

function DeltaCell({ row }) {
	const t = canonicalEventType(row.event_type)
	if (t === 'setup_change') {
		// Deliberately not a number. An edit moved no price by itself;
		// putting anything in the money column would be read as one.
		return (
			<Typography sx={{ fontSize: 13, fontWeight: 700,
			                  color: dsColorNew.textMuted, textTransform: 'capitalize' }}>
				{row.setup?.action || '—'}
			</Typography>
		)
	}
	if (t === 'applied' || t === 'tethered' || t === 'pms_error') {
		const d = row.delta ?? (row.new_price - (row.previous_price ?? row.new_price))
		const sign = d > 0 ? '+' : ''
		return (
			<Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
				<Typography sx={{ fontWeight: 700, fontSize: 14,
				                  color: d > 0 ? dsColorNew.successText
				                       : d < 0 ? dsColorNew.dangerRed
				                       : dsColorNew.textMuted }}>
					{sign}{d?.toFixed(2)}€
				</Typography>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle }}>
					→ {row.new_price?.toFixed(2)}€
				</Typography>
			</Box>
		)
	}
	if (t === 'capped') {
		return (
			<Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5 }}>
				<Typography sx={{ fontSize: 13, color: dsColorNew.textSubtle,
				                  textDecoration: 'line-through',
				                  fontFamily: 'inherit' }}>
					{row.proposed_price?.toFixed(2)}€
				</Typography>
				<Typography sx={{ fontWeight: 700, fontSize: 14,
				                  color: dsColorNew.noteAmber,
				                  fontFamily: 'inherit' }}
				            title={`Capped at limit ${row.limit?.value?.toFixed(2)}€`}>
					↳ {row.capped_to?.toFixed(2)}€
				</Typography>
			</Box>
		)
	}
	if (t === 'quarantined') {
		const d = row.delta ?? 0
		return (
			<Typography sx={{ fontSize: 12, color: dsColorNew.appBlue, fontWeight: 700,
			                  fontFamily: 'inherit' }}
			            title="Dry-run — nothing was pushed">
				would be {d > 0 ? '+' : ''}{d?.toFixed(2)}€
			</Typography>
		)
	}
	return <Typography sx={{ color: dsColorNew.textSubtle }}>—</Typography>
}

// The row actions as the app's: a grey icon that turns app blue on hover (red for the one that throws away)
const rowActionSx = { color: dsColorNew.textMuted, borderRadius: '8px',
                      '&:hover': { color: dsColorNew.appBlue, bgcolor: dsColorNew.hoverBgLight } }
const rowActionDangerSx = { ...rowActionSx, '&:hover': { color: dsColorNew.dangerRed, bgcolor: dsColorNew.dangerBg } }

function ResolutionCell({ row, onResolve, liveMode }) {
	if (canonicalEventType(row.event_type) === 'setup_change') {
		const by = row.setup?.actor
		const machine = row.setup?.actor_source === 'sync'
		return (
			<Typography sx={{ fontSize: 13,
			                  color: machine ? dsColorNew.textSubtle : dsColorNew.textMuted }}>
				{machine ? 'engine' : (by || '—')}
			</Typography>
		)
	}
	// Brokers compete (CU-869ffwj71): a decision another rate card outranked
	// is not actionable — no Override, no Retry, no Acknowledge — whatever
	// its type, so this comes before every branch that offers a button. A
	// capped loser offering Override would write over the winner.
	if (row.write_status === 'outranked') return <OutrankedCell row={row}/>
	const t = canonicalEventType(row.event_type)
	if (t === 'capped') {
		const ov = overridable(row.cap_chain)
		if (!row.resolution && ov.can) {
			const simulated = liveMode === false
			return (
				<span title={simulated
					? 'Override is only available in Live: in simulation nothing is sent, and "overridden" means "sent".'
					: 'Override — push the original proposed price on top of the cap, for this one hit'}>
					<IconButton size="small" disabled={simulated}
					            aria-label="Override this capped decision"
					            onClick={() => onResolve(row, 'override')}
					            sx={{ color: dsColorNew.successText, '&:hover': { bgcolor: dsColorNew.successBg } }}>
						<PublishIcon fontSize="small"/>
					</IconButton>
				</span>
			)
		}
		if (!row.resolution && ov.onlyConsistency) {
			// Not a dash: next to an Override button a dash reads as "the
			// button is missing", which is what Thibault reported. The
			// ladder kept this one in order, and that is said.
			return (
				<Stack direction="row" alignItems="center" gap={0.5}>
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}
					            title="Capped by category consistency only — an override does not jump the ladder">
						Kept in order
					</Typography>
					<SetupLink tab="category-consistency"/>
				</Stack>
			)
		}
		// Terminal state — the cap has been resolved somehow.
		// No resolution at all is not "discarded". Real engine events carry
		// none — that workflow is a mock with no backend — and this
		// fallback told the operator a cap had been thrown away when
		// nobody had touched it. Unknown renders as unknown.
		const meta = RESOLUTION_META[row.resolution]
		return meta ? <ResolutionChip meta={meta} row={row}/> : <Unresolved/>
	}
	if (t === 'quarantined') {
		if (row.resolution === 'pending') {
			const simulated = liveMode === false
			return (
				<Stack direction="row" gap={0.25}>
					<span title={simulated
						? 'Approve is only available in Live: in simulation nothing is sent, and "approved" means "sent".'
						: `Approve — push the held price (${eur(row.new_price)}/day, computed when it was held) to the PMS now`}>
						<IconButton size="small" disabled={simulated}
						            aria-label="Approve this quarantined decision"
						            onClick={() => onResolve(row, 'approve')}
						            sx={rowActionSx}>
							<PublishIcon fontSize="small"/>
						</IconButton>
					</span>
					<IconButton size="small"
					            title="Discard — the PMS keeps its current price; the next run may propose this change again"
					            aria-label="Discard this quarantined decision"
					            onClick={() => onResolve(row, 'discard')}
					            sx={rowActionDangerSx}>
						<DiscardIcon fontSize="small"/>
					</IconButton>
				</Stack>
			)
		}
		// No resolution at all is not "discarded". Real engine events carry
		// none — that workflow is a mock with no backend — and this
		// fallback told the operator a cap had been thrown away when
		// nobody had touched it. Unknown renders as unknown.
		const meta = RESOLUTION_META[row.resolution]
		return meta ? <ResolutionChip meta={meta} row={row}/> : <Unresolved/>
	}
	if (t === 'applied' || t === 'tethered') {
		return <SentChip row={row}/>
	}
	if (t === 'pms_error' || t === 'tethered_pms_error') {
		const isPending = !row.resolution || row.resolution === 'pending' || row.resolution === 'n/a'
		if (isPending) {
			return (
				<Stack direction="row" gap={0.25}>
					<IconButton size="small"
					            title="Retry — send the push again"
					            onClick={() => retryPmsErrorRows([row.id])}
					            sx={rowActionSx}>
						<RetryIcon fontSize="small"/>
					</IconButton>
					<IconButton size="small"
					            title={t === 'tethered_pms_error'
					              ? 'Acknowledge — leaves the system inconsistent, stops the alarm'
					              : 'Acknowledge — accept the failure, move on'}
					            onClick={() => ackPmsErrorRows([row.id])}
					            sx={rowActionSx}>
						<DiscardIcon fontSize="small"/>
					</IconButton>
				</Stack>
			)
		}
		// No resolution at all is not "discarded". Real engine events carry
		// none — that workflow is a mock with no backend — and this
		// fallback told the operator a cap had been thrown away when
		// nobody had touched it. Unknown renders as unknown.
		const meta = RESOLUTION_META[row.resolution]
		return meta ? <ResolutionChip meta={meta}/> : <Unresolved/>
	}
	return <Typography sx={{ color: dsColorNew.textSubtle, fontSize: 13 }}>—</Typography>
}

//: Decided is the event row; sent is a row of the write log pointing
//: back at it, written when the search closed (CU-869fb9wz6). The
//: server fills `write_status`: `ok`, `error`, `dry_run`, or
//: `not_sent` when no write row exists yet.
const SENT_STYLE = {
	ok:       { tone: 'ok', label: 'sent' },
	dry_run:  { tone: 'accent', label: 'simulated' },
	error:    { tone: 'err', label: 'refused' },
	not_sent: { tone: 'info', label: 'not sent' },
	superseded: { tone: 'neutral', label: 'superseded' },
	// Decided, and the PMS already held that amount — so no call was
	// made. Not a refusal: writing it would have changed nothing and
	// split the operator's date range for nothing (Thibault).
	unchanged: { tone: 'neutral', label: 'already set' },
	// Decided, and another rate card's strategy took the bracket — by
	// priority, or by the bigger move on a tie (CU-869ffwj71). Grey like
	// the other two terminal non-sends: nothing went wrong. Not
	// `superseded`, which already means "a newer search went out first".
	outranked: { tone: 'neutral', label: 'outranked' },
}

const hhmm = (iso) => {
	if (!iso) return ''
	const d = new Date(iso)
	return Number.isNaN(d.getTime()) ? '' : format(d, 'HH:mm')
}

function SentChip({ row }) {
	const status = row.write_status || (row.simulated ? 'dry_run' : null)
	if (!status) return <Unresolved/>
	const s = SENT_STYLE[status] || SENT_STYLE.error
	const when = hhmm(row.sent_at)
	const title = status === 'outranked'
		? (outrankedTitle(row) || 'Decided, never sent: another rate card\u2019s strategy outranked it on this bracket.')
		: status === 'unchanged'
		? 'Not sent on purpose: the pricelist already held this amount, so the call would have changed nothing.'
		: status === 'not_sent'
		? 'Decided, not sent yet: the push waits for every task of the search, or for its deadline.'
		: status === 'superseded'
		  ? 'Never sent: a newer search for the same office and pickup date was sent first, so this decision went stale.'
		: status === 'error'
		  ? `The PMS refused the call${row.response_code ? ` (HTTP ${row.response_code})` : ''}.`
		  : status === 'dry_run'
		    ? 'Simulation: composed and recorded, never sent.'
		    : `Sent to the PMS${when ? ` at ${when}` : ''}${row.response_code ? ` (HTTP ${row.response_code})` : ''}.`
	return (
		<Chip size="small" title={title}
		      label={when && !['not_sent', 'superseded', 'unchanged', 'outranked'].includes(status) ? `${s.label} · ${when}` : s.label}
		      sx={toneChipSx(s.tone)}/>
	)
}

//: The loser's Resolution cell: the chip, and under it by whom and on
//: which broker — pattern of `capped_by` on the Event column. Without the
//: line, "outranked" alone would make the operator open every row to
//: learn who (UX obligatory 6, CU-869ffwj71).
function OutrankedCell({ row }) {
	const p = outrankedRowParts(row)
	return (
		<Box>
			<SentChip row={row}/>
			{p && (
				<Typography sx={{ fontSize: 12, mt: 0.4, color: dsColorNew.textMuted,
				                  lineHeight: 1.3 }}
				            title={outrankedTitle(row)}>
					{p.lead} <strong>{p.strategy}</strong> {p.tail}
				</Typography>
			)}
		</Box>
	)
}

/**
 * The brackets of one PMS call. Seventeen rows for a seventeen-tier
 * pricelist: the ones this pass decided, and the ones read from the
 * pricelist seconds before so the call carried the whole ladder.
 */
function CallDialog({ callId, onClose }) {
	const [state, setState] = useState({ loading: true, rows: [] })
	useEffect(() => {
		let alive = true
		activityActions.call(callId)
			.then(b => alive && setState({ loading: false, rows: b?.brackets || [] }))
			.catch(e => alive && setState({ loading: false, rows: [], error: e?.message || 'Could not load the call' }))
		return () => { alive = false }
	}, [callId])
	const first = state.rows[0]
	return (
		<AppDialog open onClose={onClose} maxWidth="sm" paperSx={setupScopeSx}>
			<AppDialogHeader icon="plug" title="One call to the PMS" onClose={onClose}/>
			<AppDialogBody sx={{ ...dialogBodySx, borderTop: `1px solid ${dsColorNew.grayBorder}`, pt: 2 }}>
				{state.loading && <AppTableSkeleton rows={4} columns={3}/>}
				{state.error && <AppNote severity="error">{state.error}</AppNote>}
				{first && (
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mb: 1.5 }}>
						<code>{first.ladder_key}</code> · {first.origin === 'run_now' ? 'pushed by hand'
						  : first.origin === 'quarantine' ? 'approved from quarantine'
						  : first.origin === 'override' ? 'pushed by override' : 'scheduled pass'}
						{' · '}{first.status === 'ok' ? `HTTP ${first.response_code}` : first.status}
					</Typography>
				)}
				{state.rows.length > 0 && (
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { ...headCellSx, fontSize: 12 } }}>
								<TableCell>Days</TableCell>
								<TableCell align="right">€/day</TableCell>
								<TableCell>From</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{state.rows.map(b => (
								<TableRow key={b.position}>
									<TableCell sx={{ fontFamily: 'inherit' }}>
										{b.duration_from}{b.duration_to == null ? '+' : b.duration_to !== b.duration_from ? `–${b.duration_to}` : ''}
									</TableCell>
									<TableCell align="right" sx={{ fontFamily: 'inherit',
									                               fontWeight: b.source === 'decided' ? 800 : 400 }}>
										{b.amount == null ? '—' : Number(b.amount).toFixed(2)}
									</TableCell>
									<TableCell>
										<Chip size="small" label={b.source === 'decided' ? 'decided now' : 'kept from pricelist'}
										      sx={toneChipSx(b.source === 'decided' ? 'ok' : 'neutral')}/>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
				{first?.raw_response && first.status !== 'ok' && (
					<AppNote severity="error" sx={{ mt: 1.5 }}>
						{first.raw_response.message || first.raw_response._text || JSON.stringify(first.raw_response)}
					</AppNote>
				)}
			</AppDialogBody>
			<AppDialogFooter><AppDialogCancel onClick={onClose}>Close</AppDialogCancel></AppDialogFooter>
		</AppDialog>
	)
}

function DetailPush({ row }) {
	// The call to show: this row's own, or — on an outranked row — the
	// winner's, which is the one that carried the ladder (CU-869ffwj71).
	const [openCall, setOpenCall] = useState(null)
	const { reveal } = useContext(ActivityNavContext)
	const status = row.write_status || (row.simulated ? 'dry_run' : null)
	if (!status) return null
	const ob = outrankedBy(row)
	const hint = { execution_id: row.scope?.execution_id, office: row.scope?.office, sipp: row.scope?.sipp }
	const losers = wonOverEntries(row)
	return (
		<Box sx={detailBoxSx}>
			<DetailTitle>PMS push</DetailTitle>
			<Stack direction="row" gap={1.5} alignItems="center" flexWrap="wrap">
				<SentChip row={row}/>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, flex: 1 }}>
					{ob
						? outrankedSentence(row, { when: hhmm(row.sent_at) })
						: status === 'not_sent'
						? 'This decision is one bracket of a ladder the PMS only takes whole. It goes out when every task of its search has decided, or when the deadline passes.'
						: status === 'dry_run'
						  ? 'Simulation: the ladder was composed and recorded, nothing was sent.'
						  : 'One call carried the whole ladder: this bracket with the new price, the others with what the pricelist held.'}
				</Typography>
				{ob ? (
					<>
						<Button size="small" variant="outlined"
						        onClick={() => reveal(ob.event_id, hint)}
						        sx={detailButtonSx}>
							Show the winning decision
						</Button>
						{ob.call_id && (
							<Button size="small" variant="outlined" onClick={() => setOpenCall(ob.call_id)}
							        sx={detailButtonSx}>
								Show the call
							</Button>
						)}
					</>
				) : row.call_id && (
					<Button size="small" variant="outlined" onClick={() => setOpenCall(row.call_id)}
					        sx={detailButtonSx}>
						Show the call
					</Button>
				)}
			</Stack>
			{/* The truth behind the arbitration: the tariff has one net, and
			    this row's broker publishes the winner's amount with its own
			    markup, wherever that lands it. */}
			{ob && (
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1 }}>
					{oneNetNote(row)}
				</Typography>
			)}
			{/* The winner says over whom it won, and that the losers are still
			    in the feed. A winner with no rival shows nothing new. */}
			{losers.length > 0 && (
				<Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
						{wonOverSentence(row)}
					</Typography>
					{losers.map((w, i) => w.event_id && (
						<Button key={w.event_id || i} size="small" variant="text"
						        onClick={() => reveal(w.event_id, hint)}
						        sx={{ fontWeight: 600, py: 0 }}>
							{losers.length === 1 ? 'Show it' : `Show ${w.strategy_name || 'it'} on ${w.broker || '—'}`}
						</Button>
					))}
				</Stack>
			)}
			{/* After an override a decision has two sends — the pass's and
			    the override's — and the one that failed must stay visible
			    (UX obs. 5, CU-869fdg4nu). One line per origin, oldest first. */}
			{(row.sends || []).length > 1 && (
				<Stack gap={0.25} sx={{ mt: 1 }}>
					{row.sends.map((w, i) => (
						<Typography key={i} sx={{ fontSize: 12,
						                          // Red is for a refusal; a later `outranked` on a
						                          // decision that had gone out is not one (plan § 1.6).
						                          color: w.status === 'error' ? dsColorNew.dangerRed : dsColorNew.textMuted }}>
							{w.origin === 'override' ? 'Override' : w.origin === 'quarantine' ? 'Approve'
							  : w.origin === 'run_now' ? 'Pushed by hand' : 'Pass'}
							{' · '}{hhmm(w.sent_at)}{' · '}
							{w.status === 'ok' ? `HTTP ${w.response_code}` : `${w.status}${w.response_code ? ` (HTTP ${w.response_code})` : ''}`}
						</Typography>
					))}
				</Stack>
			)}
			{openCall && <CallDialog callId={openCall} onClose={() => setOpenCall(null)}/>}
		</Box>
	)
}

function DetailLadderEvent({ row }) {
	const p = row.payload || {}
	const brackets = Array.isArray(p.brackets) ? p.brackets : []
	return (
		<Box sx={detailBoxSx}>
			<DetailTitle>{row.event_type === 'ladder_conflict' ? 'Two decisions, one bracket' : 'The pricelist moved'}</DetailTitle>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mb: 1 }}>
				{row.event_type === 'ladder_conflict'
					? `${p.reason || 'Two decisions disagreed on the same bracket'}. Nothing was written for this ladder.`
					: `Before writing, the pricelist did not hold what we last wrote${p.last_written_at ? ` (${hhmm(p.last_written_at)})` : ''}. Somebody else moved it; it was written over with the value read.`}
			</Typography>
			{brackets.length > 0 && (
				<Table size="small">
					<TableBody>
						{brackets.map((b, i) => (
							<TableRow key={i}>
								<TableCell sx={{ fontFamily: 'inherit' }}>
									{b.bracket?.[0]}{b.bracket?.[1] == null ? '+' : `–${b.bracket[1]}`} days
								</TableCell>
								<TableCell>we wrote <strong>{b.last_written}</strong></TableCell>
								<TableCell>pricelist had <strong>{b.read}</strong></TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
			{p.ladder_key && <Typography sx={{ fontSize: 12, color: dsColorNew.textSubtle, mt: 1 }}><code>{p.ladder_key}</code></Typography>}
		</Box>
	)
}

function Unresolved() {
	return (
		<Typography sx={{ color: dsColorNew.textSubtle, fontSize: 13 }}
		            title="Nothing to resolve: the engine recorded this and no action is pending.">
			—
		</Typography>
	)
}

function ResolutionChip({ meta, row }) {
	// `<action> · who · when` once the feed carries them (CU-869fdg4nu);
	// rows resolved before that keep the bare label.
	const who = row?.resolution_actor
	const when = row?.resolution_at ? hhmm(row.resolution_at) : null
	const label = [meta.label, who, when].filter(Boolean).join(' · ')
	const title = row?.resolution_note ? `${meta.tooltip} Reason: ${row.resolution_note}` : meta.tooltip
	return (
		<Chip size="small"
		      icon={meta.Icon ? <meta.Icon/> : undefined}
		      label={label}
		      title={title}
		      sx={toneChipSx(meta.tone)}/>
	)
}

const RESOLUTION_META = {
	retried: {
		label: 'Retried',
		tooltip: 'Push retried successfully after failure — a fresh event was emitted.',
		tone: 'ok', Icon: RetryIcon,
	},
	acknowledged: {
		label: 'Acknowledged',
		tooltip: 'Human ack — failure noted, no further automated action.',
		tone: 'neutral', Icon: DiscardIcon,
	},
	capped: {
		label: 'Capped',
		tooltip: 'Push happened at the limit value instead of the proposed price.',
		tone: 'warn', Icon: CapIcon,
	},
	manual_override: {
		label: 'Override',
		tooltip: 'Overridden — the price before the cap was pushed for this one hit.',
		tone: 'ok', Icon: DoneIcon,
	},
	applied_manually: {
		label: 'Applied',
		tooltip: 'Quarantine cleared manually — proposed price was pushed.',
		tone: 'ok', Icon: PublishIcon,
	},
	// Legacy compat.
	approved: {
		label: 'Approved',
		tooltip: 'Approved (legacy).',
		tone: 'ok', Icon: DoneIcon,
	},
	discarded: {
		label: 'Discarded',
		tooltip: 'Discarded — nothing pushed.',
		tone: 'err', Icon: DiscardIcon,
	},
}

// ---------------------------------------------------------------------------
// Inline detail (per event type)
// ---------------------------------------------------------------------------
//: The events that end in a number the rail can explain. The rest —
//: a setup edit, a missing rate card — have no chain to draw.
const DERIVED_TYPES = new Set([
	'applied', 'tethered', 'capped', 'pms_error', 'tethered_pms_error',
	'no_price_change',
])

/**
 * What was edited, by whom, and exactly what moved.
 *
 * The audit table has carried `before`, `after` and `changed_fields`
 * since Setup shipped and nothing ever read them — there was no
 * endpoint and no screen (2026-09-28). Only the fields that changed
 * are listed: dumping two whole snapshots would bury the one line the
 * reader came for.
 */
function DetailSetupChange({ row }) {
	const c = row.setup || {}
	const fields = c.changed_fields || []
	const machine = c.actor_source === 'sync'
	const fmt = (v) =>
		v === null || v === undefined ? '—'
		: typeof v === 'object' ? JSON.stringify(v)
		: String(v)

	return (
		<Box sx={{ ...detailBoxSx, borderLeft: `4px solid ${dsColorNew.textSubtle}` }}>
			<DetailTitle>Setup change</DetailTitle>
			<Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: 1.5 }}>
				<KV k="What" v={c.entity_label}/>
				{c.name && <KV k="Name" v={c.name}/>}
				<KV k="Action" v={c.action}/>
				<KV k="By" v={machine ? 'the engine' : (c.actor || '—')}/>
			</Stack>

			{c.action === 'update' && fields.length > 0 && (
				<>
					<DetailTitle>Changed ({fields.length})</DetailTitle>
					<Stack gap={0.75} sx={{ mb: 1.5 }}>
						{fields.map(f => (
							<Stack key={f} direction="row" gap={1} alignItems="baseline"
							       flexWrap="wrap">
								<Box component="code"
								     sx={{ fontFamily: 'inherit',
								           fontSize: 12, fontWeight: 700,
								           minWidth: 180 }}>
									{f}
								</Box>
								<Typography sx={{ fontSize: 12,
								                  color: dsColorNew.textMuted }}>
									{fmt(c.before?.[f])}
								</Typography>
								<Typography sx={{ fontSize: 12,
								                  color: dsColorNew.textSubtle }}>→</Typography>
								<Typography sx={{ fontSize: 12, fontWeight: 700 }}>
									{fmt(c.after?.[f])}
								</Typography>
							</Stack>
						))}
					</Stack>
				</>
			)}

			{row.note && (
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
				                  mb: 1.5 }}>
					Reason: {row.note}
				</Typography>
			)}

			<JsonBox title={c.action === 'delete' ? 'Deleted' : 'After'}
			         data={c.action === 'delete' ? c.before : c.after}/>
		</Box>
	)
}

function EventDetail({ row }) {
	// An edit is not a decision: it has no scope, no market and no
	// price, so none of the blocks below apply to it.
	if (row.event_type === 'setup_change') return <DetailSetupChange row={row}/>
	const t = canonicalEventType(row.event_type)
	return (
		<Stack gap={1.5}>
			<DetailScope row={row}/>
			{/* The two "shipped out of order" warnings live here, not inside
			    `DetailDerivation`: that block returns `TetherDerivation` early
			    for a child, so a tethered row could never show one
			    (CU-869ffwfb9). Gated as before — the floor one never on a
			    quarantined row, whose "pushed" would be false since the ladder
			    runs before the hold. Order: Scope → warning → Via tether. */}
			{DERIVED_TYPES.has(t) && t !== 'quarantined' && <FloorVsOrderWarning row={row}/>}
			{t === 'tethered' && <SpreadVsOrderWarning row={row}/>}
			{row.via_tether       && <DetailViaTether row={row}/>}
			{DERIVED_TYPES.has(t) && <DetailDerivation row={row}/>}
			{t === 'applied'            && <DetailApplied row={row}/>}
			{t === 'tethered'           && <DetailApplied row={row}/>}
			{t === 'pms_error'          && <DetailApplied row={row}/>}
			{t === 'tethered_pms_error' && <DetailApplied row={row}/>}
			{t === 'capped'          && <DetailCapped row={row}/>}
			{(t === 'applied' || t === 'tethered' || t === 'capped') && <DetailPush row={row}/>}
			{(t === 'ladder_drift' || t === 'ladder_conflict') && <DetailLadderEvent row={row}/>}
			{t === 'quarantined'     && <DetailQuarantined row={row}/>}
			{t === 'no_price_change' && <DetailReason row={row}/>}
			{t === 'no_strategy'     && <DetailReason row={row}/>}
			{t === 'no_rate_card'    && <DetailReason row={row}/>}
		</Stack>
	)
}

function DetailViaTether({ row }) {
	const vt = row.via_tether || {}
	const p  = vt.parent_snapshot || null
	// The engine's snapshot carries money as strings and, before
	// 2026-10-03, no `delta` at all — the first real tethered event
	// crashed this block on `vt.delta.k`. Numbers are coerced and a
	// missing spread is derived from the two prices, labelled as such.
	const parentPrice = vt.parent_price == null ? null : Number(vt.parent_price)
	const newPrice = row.new_price == null ? null : Number(row.new_price)
	const delta = vt.delta
		? { k: vt.delta.k, v: Number(vt.delta.v || 0), derived: false }
		: { k: 'abs', v: (parentPrice != null && newPrice != null) ? +(newPrice - parentPrice).toFixed(2) : 0, derived: true }
	const fmt = (n) => (n == null ? '—' : n.toFixed(2))
	const formula = delta.k === 'pct'
		? `${fmt(parentPrice)}€ × ${(1 + delta.v / 100).toFixed(3)} = ${fmt(newPrice)}€`
		: `${fmt(parentPrice)}€ ${delta.v >= 0 ? '+' : '−'} ${Math.abs(delta.v)}€ = ${fmt(newPrice)}€`
	return (
		<Box sx={{ ...detailBoxSx, bgcolor: alpha(dsColorNew.cyan, 0.06),
		           borderColor: alpha(dsColorNew.cyan, 0.35), borderLeft: `4px solid ${dsColorNew.cyan}` }}>
			<DetailTitle>Via tether · derived price</DetailTitle>

			{/* Tether rule ---------------------------------------------- */}
			<Box sx={{ mb: 1.5 }}>
				<Typography sx={{ fontSize: 13, fontWeight: 700,
				                  color: dsColorNew.cyanHover }}>
					{vt.tether_name}
				</Typography>
				<Typography sx={{ fontSize: 12, color: dsColorNew.cyanHover, mt: 0.5 }}>
					Dimension: <strong>{vt.dimension}</strong> ·{' '}
					<Box component="code" sx={{ fontFamily: 'inherit',
					                            fontWeight: 700 }}>
						{vt.parent_value}
					</Box>
					{' → '}
					<Box component="code" sx={{ fontFamily: 'inherit',
					                            fontWeight: 700 }}>
						{vt.child_value}
					</Box>
					{' · spread '}
					<strong>
						{delta.v > 0 ? '+' : ''}{delta.v}
						{delta.k === 'pct' ? '%' : '€'}
					</strong>
					{delta.derived && (
						<Box component="span" sx={{ color: dsColorNew.textSubtle, ml: 0.5 }}>
							(from the two prices; this event predates the recorded spread)
						</Box>
					)}
				</Typography>
			</Box>

			{/* Formula --------------------------------------------------- */}
			<Box sx={{ p: 1, mb: 1.5, bgcolor: dsColorNew.white,
			           border: `1px dashed ${alpha(dsColorNew.cyan, 0.45)}`, borderRadius: 1 }}>
				<Typography sx={{ fontSize: 11, fontWeight: 700,
				                  textTransform: 'uppercase', letterSpacing: '0.06em',
				                  color: dsColorNew.textMuted, mb: 0.25 }}>
					Formula
				</Typography>
				<Box component="code" sx={{ fontFamily: 'inherit',
				                            fontSize: 13, fontWeight: 700,
				                            color: dsColorNew.textTitle }}>
					{formula}
				</Box>
			</Box>

			{/* Parent snapshot ------------------------------------------- */}
			{p && (
				<Box sx={{ p: 1.25, bgcolor: dsColorNew.white,
				           border: `1px solid ${alpha(dsColorNew.cyan, 0.45)}`, borderRadius: 1 }}>
					<Stack direction="row" alignItems="center"
					       justifyContent="space-between" sx={{ mb: 0.75 }}>
						<Typography sx={{ fontSize: 11, fontWeight: 700,
						                  textTransform: 'uppercase', letterSpacing: '0.06em',
						                  color: dsColorNew.textMuted }}>
							Parent event · what triggered this
						</Typography>
						{p.scope?.search_id && (
							<Button size="small" variant="outlined"
							        component="a"
							        href={realTimeSearchHref(p.scope.search_id)}
							        target="_blank" rel="noopener noreferrer"
							        startIcon={<OpenSearchIcon/>}
							        sx={detailButtonSx}>
								Parent's rate shop
							</Button>
						)}
					</Stack>
					<Stack direction="row" gap={2} flexWrap="wrap" sx={{ mb: 1 }}>
						<KV k="Parent scope"
						    v={`${p.scope?.office || '—'} · ${p.scope?.sipp || '—'}`}/>
						<KV k="Rate shop source" v={p.scope?.source || '—'}/>
						<KV k="Strategy" v={p.strategy_name || 'None'}/>
					</Stack>
					<Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: 0.5 }}>
						{p.previous_price != null && (
							<KV k="Parent previous" v={`${p.previous_price.toFixed(2)}€`}/>
						)}
						{p.new_price != null && (
							<KV k="Parent new" v={`${p.new_price.toFixed(2)}€`}
							    highlight={p.new_price > (p.previous_price ?? 0) ? 'up' : 'down'}/>
						)}
						<KV k="Writes pushed" v={p.writes_count ?? 0}/>
					</Stack>
					{vt.parent_event_id && (
						<Typography sx={{ fontSize: 11, color: dsColorNew.textMuted, mt: 0.5 }}>
							Parent event id:{' '}
							<Box component="code" sx={{ fontFamily: 'inherit',
							                            fontWeight: 700 }}>
								{vt.parent_event_id}
							</Box>
						</Typography>
					)}
				</Box>
			)}
		</Box>
	)
}

function DetailScope({ row }) {
	const face = useFaceLabel(row)
	return (
		<Box sx={detailBoxSx}>
			<Stack direction="row" alignItems="center" justifyContent="space-between"
			       sx={{ mb: 0.75 }}>
				<DetailTitle>Scope</DetailTitle>
				{row.scope?.search_id && (
					<Button size="small" variant="outlined"
					        component="a"
					        href={realTimeSearchHref(row.scope.search_id)}
					        target="_blank" rel="noopener noreferrer"
					        startIcon={<OpenSearchIcon/>}
					        sx={detailButtonSx}>
						Open in Real Time Searches
					</Button>
				)}
			</Stack>
			<KV k="Rate shop source" v={row.scope?.source}/>
			<KV k="Office" v={row.scope?.office}/>
			<KV k="ACRISS" v={row.scope?.sipp}/>
			<KV k="Pickup date" v={row.scope?.pickup_date}/>
			<KV k="Duration" v={row.scope?.duration_days ? `${row.scope.duration_days} days` : '—'}/>
			{face && <KV k="Rate card" v={face}/>}
			<KV k="Strategy" v={row.strategy_name || 'None matched'}/>
			{/* The row the engine took when it could not judge like with
			    like (CU-869fh646w, `own_kind_unknown_*`): the Market block
			    is not painted — no market was read — so the row is named
			    here. Totals for the stay, like every other block. */}
			{isOwnKindUnknown(row.payload?.reason) && row.derivation?.own && (
				<KV k="Our row"
				    v={`${row.derivation.own.car || 'our row'} · ${eur(totalOf(row.derivation.own.pvp, row.scope?.duration_days))}`}/>
			)}
			{row.scope?.search_id && (
				<KV k="Search ID"
				    v={<Box component="code" sx={{ fontSize: 12,
				                                    fontFamily: 'inherit' }}>
				         {row.scope.search_id}
				       </Box>}/>
			)}
		</Box>
	)
}

function DetailApplied({ row }) {
	const isTetheredError = row.event_type === 'tethered_pms_error'
	return (
		<Box sx={detailBoxSx}>
			{isTetheredError && (
				<Box sx={{ p: 1.5, mb: 1.5,
				           bgcolor: dsColorNew.dangerRed, color: dsColorNew.white,
				           border: `2px solid ${dsColorNew.dangerRed}`, borderRadius: 1 }}>
					<Stack direction="row" gap={1} alignItems="flex-start">
						<BrokenIcon sx={{ color: dsColorNew.dangerBorder, mt: 0.25 }}/>
						<Box>
							<Typography sx={{ fontWeight: 700, fontSize: 14,
							                  color: dsColorNew.white, mb: 0.5 }}>
								System inconsistent
							</Typography>
							<Typography sx={{ fontSize: 12, color: dsColorNew.dangerBorder,
							                  lineHeight: 1.5 }}>
								The parent applied successfully but this tethered child
								push failed. Parent and child are now <strong>out of sync</strong>
								{' '}in the PMS — the spread declared by the tether is broken.
								Retry the push or reconcile manually.
							</Typography>
						</Box>
					</Stack>
				</Box>
			)}
			<DetailTitle>
				Price push {row.event_type === 'pms_error' ? '· ERROR'
				         : isTetheredError                ? '· ERROR (child)' : ''}
			</DetailTitle>
			{row.error && (
				<Box sx={{ p: 1, bgcolor: dsColorNew.dangerBg, border: `1px solid ${dsColorNew.dangerBorder}`,
				           borderRadius: 1, mb: 1.5 }}>
					<Typography sx={{ fontWeight: 700, color: dsColorNew.dangerRed,
					                  fontSize: 12 }}>
						{row.error.api} → {row.error.code} · {row.error.message}
					</Typography>
				</Box>
			)}
			<DetailTitle>Writes ({(row.writes || []).length})</DetailTitle>
			<Stack gap={1}>
				{(row.writes || []).map((w, i) => (
					<WriteBlock key={i} write={w}/>
				))}
			</Stack>
		</Box>
	)
}

// ---------------------------------------------------------------------------
// Price derivation — the chain from the market to the number we push
// ---------------------------------------------------------------------------

//: Caps that act on the shelf price, before the markup is removed. The
//: rest act after. The distinction is not cosmetic: putting a PVP-stage
//: cap below the conversion would show a euro amount that never existed
//: at that point in the chain. `price_limit` joined this set on
//: 2026-10-05 (CU-869fbzw60): the floors are shelf prices and the engine
//: now applies them before the conversion, so the rail has to draw them
//: there — or the one sum the operator can check by hand stops adding up.
const PVP_STAGE_CAPS = new Set([
	'rank_cap', 'keep_position',
	'never_below_competitor', 'never_above_competitor',
	'price_limit',
])

//: The rate the driver converted by in that pass, from the event and
//: never from a constant: a wrong rate has to be visible as a number the
//: operator can check against the PMS, and an event written before a
//: change of configuration has to keep telling the truth (UX gate,
//: 2026-10-05). `null` when the PMS speaks retail on its own.
function vatRateOf(row) {
	const r = row?.derivation?.driver?.vat_rate_pct
	return r == null || r === '' ? null : Number(r)
}
function vatNote(row) {
	const r = vatRateOf(row)
	return r == null ? null : `VAT ${Number.isInteger(r) ? r : r.toFixed(2)} % included`
}

const CAP_LABELS = {
	rank_cap:               'Rank cap',
	keep_position:          'Keep-position guard',
	never_below_competitor: 'Never below competitor',
	never_above_competitor: 'Never above competitor',
	markup_cap:             'Markup limit',
	price_limit:            'Price limit',
	date_protection:        'Date protection',
	category_consistency:   'Category consistency',
}

//: A cap's label comes from its key, not its reason: a date protection
//: keeps `reason = price_limit` and says what it is in `source.kind`
//: (CU-869fe8jrk). One function, so row, rail, Limits and filter agree.
function capLabel(cap) {
	return CAP_LABELS[capKey(cap)] || cap?.reason
}

const POLICY_LABELS = { ranking: 'Ranking', market_trend: 'Market trend' }

/**
 * What a cap has to say for itself, beyond the two euro amounts.
 *
 * A node that reads "Markup limit · 23.56 € → 23.67 €" says a limit
 * fired and not which limit, which is the thing an operator wants and
 * the thing they would otherwise have to go and look up (Curro,
 * 2026-09-28). The engine already records it in the cap's `source`.
 */
function capNotes(cap, row) {
	if (capKey(cap) === 'date_protection') return protectionNotes(cap, row)
	const src = cap?.source || {}
	const out = []
	if (cap?.reason === 'markup_cap') {
		if (src.markup_limit_pct != null) {
			out.push(`rate card allows ${Number(src.markup_limit_pct).toFixed(2)} % markup`)
		}
		if (src.broker_markup_pct != null) {
			out.push(`the market priced at ${Number(src.broker_markup_pct).toFixed(2)} %`)
		}
	}
	if (cap?.reason === 'category_consistency') {
		return consistencyNotes(cap, row?.scope?.duration_days)
	}
	if (cap?.reason === 'price_limit') {
		// The direction is not stored, but it is in the numbers: a cap
		// that raised the price was a floor, one that lowered it a
		// ceiling. Stating it beats making the reader compare.
		const raised = Number(cap.to) > Number(cap.from)
		const value = src.limit_value ?? cap.to
		out.push(`${raised ? 'minimum' : 'maximum'} ${eur(value)}${src.label ? ` · ${src.label}` : ''}`)
		if (src.price_limit_id) out.push(`limit ${src.price_limit_id}`)
	}
	return out
}

const RAIL = {
	line:   dsColorNew.grayBorder,
	dot:    dsColorNew.textSubtle,
	strong: dsColorNew.textTitle,
	amber:  dsColorNew.warningBar,
	amberLine: dsColorNew.noteAmberBorder,
	amberText: dsColorNew.noteAmber,
}

function eur(n) {
	return n == null ? '—' : `${Number(n).toFixed(2)} €`
}

/** How the policy is described in one line, next to the title. */
function policyCaption(d) {
	if (!d) return null
	const bits = [POLICY_LABELS[d.policy] || d.policy]
	if (d.params?.targetPosition) bits.push(`target position ${d.params.targetPosition}`)
	if (d.params?.rankCap) bits.push(`rank cap ${d.params.rankCap}`)
	const u = d.params?.undercut
	if (u?.value != null) {
		const unit = Number(u.unit) === 0 ? '€' : '%'
		bits.push(`undercut ${u.value}${unit} ${Number(u.direction) === 0 ? 'down' : 'up'}`)
	}
	return bits.join(' · ')
}

/**
 * One segment of the rail: the line on the left, the content on the right.
 *
 * `dot` marks a milestone — an amount that existed. A segment without
 * one is arithmetic on the way to the next milestone, and it is drawn
 * dashed so the eye reads it as a transition rather than a value.
 */
function RailRow({ dot = false, tone = 'neutral', dashed = false, last = false, children }) {
	const amber = tone === 'amber'
	return (
		<Stack direction="row" gap={1.5} sx={{ alignItems: 'stretch' }}>
			<Box sx={{ position: 'relative', width: 14, flexShrink: 0 }}>
				{!last && (
					<Box sx={{
						position: 'absolute', left: 6, top: 6, bottom: 0,
						borderLeft: dashed ? '2px dashed' : '2px solid',
						borderColor: amber ? RAIL.amberLine : RAIL.line,
					}}/>
				)}
				{dot && (
					<Box sx={{
						position: 'absolute', left: 0, top: 5,
						width: 14, height: 14, borderRadius: '50%',
						bgcolor: amber ? RAIL.amber
						       : tone === 'strong' ? RAIL.strong : RAIL.dot,
						border: '2px solid', borderColor: dsColorNew.white,
						boxSizing: 'border-box',
					}}/>
				)}
			</Box>
			<Box sx={{ flex: 1, minWidth: 0, pb: last ? 0 : 2 }}>{children}</Box>
		</Stack>
	)
}

/** A milestone: a name on the left, the amount it was on the right. */
function RailNode({ label, amount, tone = 'neutral', children, last = false }) {
	const amber = tone === 'amber'
	return (
		<RailRow dot tone={tone} last={last}>
			<Stack direction="row" justifyContent="space-between"
			       alignItems="baseline" gap={2}>
				<Typography sx={{
					fontSize: 12, fontWeight: 700, textTransform: 'uppercase',
					letterSpacing: '0.06em', lineHeight: 1.6,
					color: amber ? RAIL.amberText
					     : tone === 'strong' ? dsColorNew.textTitle : dsColorNew.textMuted,
				}}>
					{label}
				</Typography>
				<Typography sx={{
					fontSize: tone === 'strong' ? 20 : 18,
					fontWeight: 700, whiteSpace: 'nowrap', lineHeight: 1.4,
					fontVariantNumeric: 'tabular-nums',
					color: amber ? RAIL.amberText : dsColorNew.textTitle,
				}}>
					{amount}
				</Typography>
			</Stack>
			{children}
		</RailRow>
	)
}

/** The arithmetic between two milestones. Never carries an amount. */
function RailStep({ operator, note, last = false }) {
	return (
		<RailRow dashed last={last}>
			<Typography sx={{ fontSize: 13, fontWeight: 700,
			                  color: dsColorNew.textMuted, lineHeight: 1.6 }}>
				{operator}
			</Typography>
			{note && <RailNote>{note}</RailNote>}
		</RailRow>
	)
}

/** Everything that is not a number: quiet, and one size down. */
function RailNote({ children }) {
	return (
		<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
		                  lineHeight: 1.6 }}>
			{children}
		</Typography>
	)
}

/**
 * Where the pushed price comes from, as one readable chain.
 *
 * The panel used to show `previous` and `new` and nothing that connected
 * them (Curro, 2026-09-28). Every number below was already on the event
 * except the market context, which the engine started recording on the
 * same day — so this renders whatever the row has and never pretends an
 * older run was unexplained.
 *
 * Two rules keep it from becoming a wall: a stage with nothing to say is
 * not drawn at all (no "undercut —", no "caps: none"), and colour means
 * exactly one thing — amber where something was attenuated, red on a
 * negative delta, neutral everywhere else.
 */
/**
 * A tethered child's chain, which is a different story entirely.
 *
 * A child never looks at a market: its price is its parent's, plus the
 * spread the tether declares. Running it through the market rail drew
 * three empty milestones and then told the operator the run "predates
 * the market trail" — which is false and the wrong kind of false, since
 * it blames the age of the data for a shape that is correct.
 */
function TetherDerivation({ row }) {
	const vt = row.via_tether
	const pct = vt?.delta?.k === 'pct'
	const v = vt?.delta?.v || 0
	const result = row.new_price ?? row.pushed_price
	const spread = pct
		? `× ${(1 + v / 100).toFixed(3)} — spread ${v > 0 ? '+' : ''}${v} %`
		: `${v >= 0 ? '+' : '−'} ${eur(Math.abs(v))} — spread`

	return (
		<Box sx={detailBoxSx}>
			<Stack direction="row" justifyContent="space-between"
			       alignItems="baseline" gap={2} sx={{ mb: 1.75 }}>
				<DetailTitle>Price derivation</DetailTitle>
				<Typography sx={{ fontSize: 12, fontWeight: 700,
				                  color: dsColorNew.textMuted, textAlign: 'right' }}>
					Tether · {vt?.tether_name || vt?.dimension}
				</Typography>
			</Stack>

			<RailNode label="1 · The parent" amount={eur(vt?.parent_price)}>
				<RailNote>
					{vt?.dimension} {vt?.parent_value} — priced on its own market
				</RailNote>
			</RailNode>

			<RailStep operator={`${eur(vt?.parent_price)} ${spread} = ${eur(result)}`}
			          note={`${vt?.parent_value} → ${vt?.child_value}`}/>

			<RailNode label="2 · Price to set in the PMS" amount={eur(result)}>
				<RailNote>what the customer will pay · inherited, never capped</RailNote>
			</RailNode>

			<RailNode label="Result" amount={eur(result)} tone="strong" last>
				{row.simulated && <RailNote>dry run — nothing written</RailNote>}
			</RailNode>
		</Box>
	)
}

function DerivationRail({ row }) {
	// Totals, like the sections above it. The engine decides in daily
	// terms and the maths is a ratio, so the division reads the same in
	// either unit — but switching units between the panel and the
	// dialog that explains it invites exactly the mistake the dialog
	// exists to prevent (Curro, 2026-09-28). The result shows both.
	const days = row.scope?.duration_days
	const tot = (v) => totalOf(v, days)
	const d = row.derivation
	const markup = row.broker_markup_pct
	const caps = row.cap_chain || []
	const pvpCaps = caps.filter(c => PVP_STAGE_CAPS.has(c.reason))
	const netCaps = caps.filter(c => !PVP_STAGE_CAPS.has(c.reason))

	const target = row.proposed_price
	const result = row.new_price ?? row.pushed_price
	// The net the conversion produced, before any cap of its own. The
	// first net-stage cap knows it exactly; with none, it is the result.
	const netBeforeCaps = netCaps.length ? netCaps[0].from : result

	if (!d && target == null && result == null) return null

	// Which percentage the engine actually divided by — not always the
	// one it measured (CU-869ffwfam).
	const factor = conversionFactor(markup, d?.limits?.markup)
	const caption = policyCaption(d)
	// The server does not send a delta; `DeltaCell` derives it the same
	// way, and the two must not disagree on the same screen.
	const delta = row.delta ?? (result == null || row.previous_price == null
		? null : result - row.previous_price)
	// How far out of the market we were. It is the punchline of most
	// rows — a 61 % cut reads as an aggressive strategy until you see
	// we were at two and a half times the cheapest competitor.
	const versusMarket = (d?.market_low && row.market_pvp)
		? Number(row.market_pvp) / Number(d.market_low) : null

	//: `null` on every ordinary decision, so the two steps below are
	//: drawn only where they say something.
	const imCopy = incompleteMarketCopy(row, days)

	// Numbered as they are rendered, so a row missing the market stage
	// starts at one rather than at two.
	let step = 0
	const n = () => ++step

	return (
		<Box sx={detailBoxSx}>
			<Stack direction="row" justifyContent="space-between"
			       alignItems="baseline" gap={2} sx={{ mb: 1.75 }}>
				<DetailTitle>Price derivation</DetailTitle>
				{caption && (
					<Typography sx={{ fontSize: 12, fontWeight: 700,
					                  color: dsColorNew.textMuted, textAlign: 'right' }}>
						{caption}
					</Typography>
				)}
			</Stack>

			{/* 1 — the market. Both sides of the comparison are
			    established before the strategy decides anything: the
			    market here, our own price next. They used to be one
			    node and a footnote, which buried the comparison that
			    explains the whole row (Curro, 2026-09-28). */}
			{d && d.reference != null && (
				<RailNode label={`${n()} · The market`} amount={eur(tot(d.reference))}>
					<RailNote>
						{d.reference_provider || 'the market'}
						{/* "cheapest" was a fixed string and it is not always
						    true: a Ranking strategy targeting any position
						    but the first anchors on somebody dearer, and the
						    incomplete-market branch anchors on the dearest of
						    all. Saying "cheapest" here, in the node where the
						    operator checks the figure, is the screen lying
						    (UX gate, 2026-10-05). */}
						{d.competitors_in_scope != null
							&& ` — ${d.incomplete_market ? 'dearest' : 'cheapest'} of ${d.competitors_in_scope} competitor${d.competitors_in_scope === 1 ? '' : 's'} in scope`}
					</RailNote>
					{imCopy && <RailNote>{imCopy.rail.node1Note}</RailNote>}
					{d.market_low != null && d.market_high != null && (
						<RailNote>
							spread {eur(tot(d.market_low))} … {eur(tot(d.market_high))}
						</RailNote>
					)}
				</RailNode>
			)}

			{/* 2 — our own price, and the markup it implies. */}
			{row.market_pvp != null && (
				<RailNode label={`${n()} · Our price today`} amount={eur(tot(row.market_pvp))}>
					<RailNote>
						shelf price in this rate shop
						{versusMarket ? ` · ${versusMarket.toFixed(1)}× the cheapest` : ''}
					</RailNote>
					{row.read_net != null && (
						<RailNote>
							price in the PMS {eur(tot(row.read_net))}
							{markup != null && ` — broker markup ${Number(markup).toFixed(2)} %`}
						</RailNote>
					)}
				</RailNode>
			)}

			{/* 3 — what the rule decides, with the undercut on the way in. */}
			{d?.steps?.filter(s => s.step === 'undercut' && s.from !== s.to)
			  .map((s, i) => (
				<RailStep key={`u${i}`}
				          operator={`undercut ${eur(tot(s.from))} → ${eur(tot(s.to))}`}
				          note={s.against ? `against ${d.reference_provider || s.against}` : null}/>
			))}

			{/* 3b — the incomplete-market branch, when it priced. It
			    replaces the undercut step above rather than joining it:
			    there is nobody at the target position to undercut, which
			    is the whole reason this branch ran. The arithmetic is
			    composed from the anchor, not from `target` — that is the
			    price after the competitor guard, and a cap belongs to the
			    cap rows below, not inside the strategy's own equation. */}
			{imCopy && (
				<RailStep operator={imCopy.rail.step} note={imCopy.rail.stepNote}/>
			)}

			{pvpCaps.map((c, i) => (
				<RailNode key={`pc${i}`} tone="amber"
				          label={capLabel(c)}
				          amount={`${eur(tot(c.from))} → ${eur(tot(c.to))}`}>
					{capNotes(c, row).map((x, j) => <RailNote key={j}>{x}</RailNote>)}
					{/* The override is a note under the cap it jumped, never a
					    rewrite of the rail: the chain keeps saying what capped
					    it (UX decision 8, CU-869fdg4nu). */}
					{row.resolution === 'manual_override' && overridable([c]).can && (
						<RailNote>
							overridden{row.resolution_actor ? ` by ${row.resolution_actor}` : ''}
							{row.resolution_at ? ` · ${hhmm(row.resolution_at)}` : ''} — this cap was jumped for this one hit
						</RailNote>
					)}
				</RailNode>
			))}

			<RailNode label={`${n()} · The strategy decides`} amount={eur(tot(target))}>
				<RailNote>{imCopy ? imCopy.rail.node3 : 'target shelf price'}</RailNote>
			</RailNode>

			{/* 4 — the conversion, written out so it can be checked. */}
			{factor != null && (
				<RailStep operator={`${eur(tot(target))} ÷ ${factor.toFixed(4)} = ${eur(tot(netBeforeCaps))}`}
				          note={conversionNote(markup, d?.limits?.markup)}/>
			)}

			<RailNode label={`${n()} · Price to set in the PMS`} amount={eur(tot(netBeforeCaps))}>
				<RailNote>what the customer will pay</RailNote>
			</RailNode>

			{netCaps.map((c, i) => (
				<RailNode key={`nc${i}`} tone="amber"
				          label={capLabel(c)}
				          amount={`${eur(tot(c.from))} → ${eur(tot(c.to))}`}>
					{capNotes(c, row).map((x, j) => <RailNote key={j}>{x}</RailNote>)}
				</RailNode>
			))}

			<RailNode label="Result" amount={days ? `${eur(tot(result))} · ${eur(result)}/day` : eur(result)} tone="strong" last>
				<Stack direction="row" gap={1} flexWrap="wrap" alignItems="baseline">
					{row.previous_price != null && (
						<RailNote>was {eur(tot(row.previous_price))}</RailNote>
					)}
					{delta != null && (
						<Typography sx={{
							fontSize: 12, fontWeight: 700, lineHeight: 1.65,
							fontVariantNumeric: 'tabular-nums',
							color: delta > 0 ? dsColorNew.successText : delta < 0 ? dsColorNew.dangerRed : dsColorNew.textMuted,
						}}>
							{delta > 0 ? '+' : ''}{Number(tot(delta)).toFixed(2)} €
						</Typography>
					)}
					{row.simulated && (
						<RailNote>· dry run — nothing written</RailNote>
					)}
				</Stack>
			</RailNode>

			{!d && (
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
				                  mt: 2, pt: 2, borderTop: '1px solid',
				                  borderColor: dsColorNew.grayBorder }}>
					This run predates the market trail, so the competitor it priced
					against was not recorded. Everything from the target price down
					is exact.
				</Typography>
			)}
		</Box>
	)
}

// ---------------------------------------------------------------------------
// The five sections: market, strategy, limits, tethering, push
// ---------------------------------------------------------------------------

/**
 * Totals, not daily rates.
 *
 * The engine decides in daily terms — that is the currency of the
 * comparison — but an operator quotes a rental, so every figure here
 * is the total for the stay (Curro, 2026-09-28). The exception is the
 * final push: the PMS stores the daily rate, and that is the number
 * somebody will check against Ranteon, so it shows both.
 *
 * Where the rate shop gave us its own total it is used verbatim;
 * `daily_price` is rounded to the cent, so `daily × days` drifts by a
 * cent or two from what the market actually displayed.
 */
function totalOf(daily, days) {
	if (daily == null) return null
	const n = Number(days)
	return n > 0 ? Number(daily) * n : Number(daily)
}

/**
 * A section, and the one line of context that governs it.
 *
 * The caption carries the two things most worth knowing before
 * reading any number — which rule ran, and what unit the amounts are
 * in — and it was grey micro-text in a corner, which is where the eye
 * does not go (Curro, 2026-09-28). It is a pill now: `strong` for the
 * strategy, because "Ranking · target position 1" explains every
 * figure under it, and quiet for the rest.
 */
function SectionBox({ title, caption, badge, tone = 'quiet', children }) {
	const strong = tone === 'strong'
	return (
		<Box sx={detailBoxSx}>
			<Stack direction="row" justifyContent="space-between"
			       alignItems="center" gap={2} sx={{ mb: 1.5 }}>
				<DetailTitle>{title}</DetailTitle>
				<Box sx={{ flex: 1 }}/>
				{/* Pinned to the caption rather than floating above the
				    section: the pill says what the strategy asked for, and
				    this says why it could not be served that way. */}
				{badge && (
					<Chip size="small" label={badge} sx={{ ...toneChipSx('info'), flexShrink: 0 }}/>
				)}
				{caption && (
					<Box sx={{
						px: 1.25, py: 0.5, borderRadius: 1,
						bgcolor: strong ? dsColorNew.pillActiveBg : dsColorNew.pillTrackBg,
						border: '1px solid',
						borderColor: strong ? dsColorNew.noteInfoBorder : dsColorNew.grayBorder,
						flexShrink: 0,
					}}>
						<Typography sx={{
							fontSize: strong ? 14 : 13,
							fontWeight: 700, lineHeight: 1.3,
							color: strong ? dsColorNew.appBlue : dsColorNew.textMuted,
						}}>
							{caption}
						</Typography>
					</Box>
				)}
			</Stack>
			{children}
		</Box>
	)
}

/** One labelled amount, with whatever has to be said beside it. */
function Line({ label, amount, note, tone = 'neutral', action, children }) {
	const amber = tone === 'amber'
	return (
		<Box sx={{ mb: 1.25, '&:last-of-type': { mb: 0 } }}>
			<Stack direction="row" alignItems="baseline" gap={2}>
				<Typography sx={{
					fontSize: 13, fontWeight: 700, minWidth: 205,
					color: amber ? RAIL.amberText : dsColorNew.textTitle,
				}}>
					{label}
				</Typography>
				<Typography sx={{
					fontSize: 18, fontWeight: 700, whiteSpace: 'nowrap',
					fontVariantNumeric: 'tabular-nums',
					color: amber ? RAIL.amberText : dsColorNew.textTitle,
				}}>
					{amount}
				</Typography>
				{note && (
					<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted }}>
						{note}
					</Typography>
				)}
				<Box sx={{ flex: 1 }}/>
				{action}
			</Stack>
			{children && <Box sx={{ pl: '221px', mt: 0.35 }}>{children}</Box>}
		</Box>
	)
}

// Context, not decoration. It was 0.82rem, which is small enough to
// be a reading test rather than a note (Curro, 2026-09-28).
function SubNote({ children }) {
	return (
		<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
		                  lineHeight: 1.6 }}>
			{children}
		</Typography>
	)
}

function setupHref(tab, id) {
	const q = new URLSearchParams({ tab })
	if (id) q.set('highlight', id)
	return `${DS_REPRICING_ROUTES.SETUP_PATH}?${q.toString()}`
}

//: `label` names the destination when two links share a line — the
//: spread-vs-order warning sends to Tethering and to the group and two
//: "Setup" side by side told the operator nothing (CU-869ffwfb9).
function SetupLink({ tab, id, label = 'Setup' }) {
	return (
		<Button variant="text" component={RouterLink} to={setupHref(tab, id)} size="small"
		        endIcon={<OpenSearchIcon sx={{ fontSize: 16 }}/>}
		        sx={{ fontWeight: 600, fontSize: 13 }}>
			{label}
		</Button>
	)
}

function MarketSection({ row, days }) {
	const d = row.derivation
	const competitors = d?.competitors || []
	const [open, setOpen] = useState(false)
	const axes = useAxisCatalog()
	// The row of ours the engine took (CU-869fh646w): one decision per
	// cell and rate card, the cheapest row by PVP among the ones the
	// strategy covers. Its car and kind go on the line, and the rows left
	// aside in one line under it — never as extra rows: "our row" is one.
	// Events before this task carry no `own` and read as they always did.
	const own = d?.own && typeof d.own === 'object' ? d.own : null
	const ownNote = own
		? [own.car, axes.prepaidName(own.tariff_type == null ? null : Boolean(Number(own.tariff_type))),
		   axes.insuranceName(own.insurance == null || Number(own.insurance) === 0 ? null : own.insurance)]
			.filter(Boolean).join(' · ')
		: null
	const discarded = discardedCopy(d?.own_discarded, own, days,
		{ prepaidName: axes.prepaidName, insuranceName: axes.insuranceName, money: eur })

	const refTotal = competitors.find(c => c.provider_id === d?.reference_provider_id)?.total_price
	const marketTotal = refTotal ?? totalOf(d?.reference, days)
	// Neither line will render, so neither will the box. An event that
	// never reached a market — no strategy covered it, no rate card
	// matched — showed an empty Market section with a unit pill in it
	// and nothing else (Curro, 2026-09-28).
	if (marketTotal == null && row.market_pvp == null) return null
	const low = totalOf(d?.market_low, days)
	const high = totalOf(d?.market_high, days)
	const ours = totalOf(row.market_pvp, days)
	const versus = (low && ours) ? ours / low : null

	return (
		<SectionBox title="Market" caption={days ? `totals for ${days} days` : null}>
			{marketTotal != null && (
				<Line label="Market price" amount={eur(marketTotal)}
				      action={competitors.length > 0 && (
					      <Button variant="text" size="small" onClick={() => setOpen(true)}
					              sx={{ fontWeight: 600, fontSize: 13 }}>
						      {competitors.length} competitor{competitors.length === 1 ? '' : 's'}
					      </Button>
				      )}>
					<SubNote>
						{d?.reference_provider || 'the market'}
						{d?.competitors_in_scope != null
							&& ` · ${d.competitors_in_scope} competitor${d.competitors_in_scope === 1 ? '' : 's'} in scope`}
						{d?.own_position != null && ` · #${d.own_position} in the whole rate shop, every category together`}
						{low != null && high != null && ` · spread ${eur(low)} … ${eur(high)}`}
					</SubNote>
				</Line>
			)}
			{ours != null && (
				<Line label="Our price" amount={eur(ours)}
				      note={[ownNote, versus ? `${versus.toFixed(1)}× the cheapest` : null].filter(Boolean).join(' · ') || null}>
					{discarded && (
						<SubNote>
							<strong>{discarded.lead}</strong>{' '}
							{discarded.items.map((item, i) => (
								<React.Fragment key={i}>
									{i > 0 && ' · '}
									<span title={item.title || undefined}>{item.text}</span>
								</React.Fragment>
							))}.
						</SubNote>
					)}
				</Line>
			)}
			<CompetitorsDialog open={open} onClose={() => setOpen(false)}
			                   row={row} days={days} competitors={competitors}
			                   ours={row.market_pvp}/>
		</SectionBox>
	)
}

/**
 * The adjustment the engine makes when nobody holds the position the
 * strategy asked for: it prices above the dearest competitor instead of
 * refusing. A step of the chain, so it sits in the chain — an alert at
 * the top is the pattern for "two rules could not both be satisfied",
 * which is not what happened here (UX gate, 2026-10-05).
 *
 * Every string it shows comes from `incompleteMarketCopy`, which is
 * pure and tested: the component only places them.
 */
function IncompleteMarketLine({ row, days }) {
	const copy = incompleteMarketCopy(row, days)
	if (!copy) return null
	return (
		<Line label={copy.label} amount={copy.amount}
		      action={<InfoIcon sx={{ fontSize: 17, color: dsColorNew.textSubtle }}
		                        titleAccess={copy.tooltip}/>}>
			{copy.subNotes.map((t, i) => <SubNote key={i}>{t}</SubNote>)}
		</Line>
	)
}

function StrategySection({ row, days }) {
	const [open, setOpen] = useState(false)
	const d = row.derivation
	const pms = totalOf(row.read_net, days)
	const decided = totalOf(row.proposed_price, days)
	if (pms == null && decided == null) return null

	return (
		<SectionBox title="Strategy" tone="strong"
		            badge={incompleteMarketCopy(row, days)?.badge}
		            caption={policyCaption(d) || row.strategy_name}>
			{pms != null && (
				<Line label="Price in the PMS" amount={eur(pms)}
				      note={vatNote(row) ? `what the customer pays today · ${vatNote(row)}`
				                         : 'what the customer pays today'}/>
			)}
			{row.broker_markup_pct != null && (
				<Line label="Markup measured"
				      amount={`${Number(row.broker_markup_pct).toFixed(2)} %`}
				      note={row.market_pvp != null && row.read_net != null
					      ? `${eur(totalOf(row.market_pvp, days))} / ${eur(pms)} − 1`
					      : null}/>
			)}
			<IncompleteMarketLine row={row} days={days}/>
			{decided != null && (
				<Line label="Price we decided" amount={eur(decided)}
				      action={
					      <Button variant="text" size="small" onClick={() => setOpen(true)}
					              startIcon={<InfoIcon sx={{ fontSize: 16 }}/>}
					              sx={{ fontWeight: 600, fontSize: 13 }}>
						      How this was calculated
					      </Button>}/>
			)}
			<CalculationDialog open={open} onClose={() => setOpen(false)} row={row}/>
		</SectionBox>
	)
}

function LimitsSection({ row, days }) {
	const limits = row.derivation?.limits
	const caps = row.cap_chain || []
	const capOf = (reason) => caps.find(c => c.reason === reason)
	const markup = limits?.markup
	const priceLimit = limits?.price_limit
	const markupCap = capOf('markup_cap')
	const limitCap = capOf('price_limit')
	// Every date protection the rental crossed, applied or not; the one
	// that did not cross is not listed — the block says what the engine
	// weighed, not what exists (UX 8d, CU-869fe8jrk).
	const protections = limits?.protections || []
	const crossing = (p) => crossingSentence({ pickup: row.scope?.pickup_date, days,
	                                           from: p.date_from, to: p.date_to })
	const outranked = (p) => p.outranked_by === 'protection'
		? `does not apply · below the ${eur(protections.find(o => o.applies)?.min)}/day protection`
		: `does not apply · below the ${priceLimit?.min != null ? `${eur(priceLimit.min)}/day` : ''} floor already in force`

	// Events written before the engine recorded the limits in force
	// still carry the chain of what actually capped them — it is how
	// the row's own chip knows to say "Markup limit". Drawing nothing
	// there left a capped event with no sign of the cap between the
	// strategy and the push (Curro, 2026-09-28). What such an event
	// cannot say is which limits did NOT fire, so it does not.
	if (!limits && caps.length > 0) {
		return (
			<SectionBox title="Limits" caption="only the caps that fired">
				{caps.map((c, i) => (
					<Line key={i} tone="amber"
					      label={capLabel(c)}
					      amount={eur(totalOf(c.to, days))}
					      note={`from ${eur(totalOf(c.from, days))}`}>
						{capNotes(c, row).map((x, j) => <SubNote key={j}>{x}</SubNote>)}
					</Line>
				))}
			</SectionBox>
		)
	}
	if (!limits || (!markup && !priceLimit && !protections.length)) return null

	return (
		<SectionBox title="Limits">
			{markup && markup.limit_pct != null && (
				<Line label="Markup limit"
				      amount={`${Number(markup.limit_pct).toFixed(2)} %`}
				      tone={markup.applies ? 'amber' : 'neutral'}
				      note={(markup.applies
					      ? `APPLIES → ${eur(totalOf(markupCap?.to, days))}`
					      : `does not apply${markup.measured_pct != null
						      ? ` · measured ${Number(markup.measured_pct).toFixed(2)} %` : ''}`)
					      + (markup.used_pct != null
						      ? ` · used ${Number(markup.used_pct).toFixed(2)} %` : '')}
				      action={<SetupLink tab="rate-cards" id={markup.face_id}/>}>
					<SubNote>rate card · face {markup.face_id || '—'}</SubNote>
				</Line>
			)}
			{/* A face with no markup cap has no "Markup limit" line, so a
			    conversion floored at zero would go unsaid — and it is the
			    case Thibault has (CU-869ffwfam). Its own line, and no
			    0.00 % presented as a limit that does not exist. */}
			{markup && markup.limit_pct == null && markup.floored_at_zero && (
				<Line label="Markup"
				      amount={`used ${Number(markup.used_pct ?? 0).toFixed(2)} %`}
				      tone="amber"
				      note={`measured ${Number(markup.measured_pct).toFixed(2)} %`}
				      action={<SetupLink tab="rate-cards" id={markup.face_id}/>}>
					<SubNote>a markup below zero is not a price to divide by — the market
						sold under our own net, so the conversion used 0 %</SubNote>
				</Line>
			)}
			{/* When a protection set the floor it is the `price_limit` block
			    itself (plan §1.4) and also `protections[0]`; drawing both
			    said "APPLIES" twice for one cap (QA A). The protection line
			    below carries it; the ordinary limit that lost is not in the
			    derivation and so is not drawn. */}
			{priceLimit && priceLimit.source !== 'protection' && (
				<Line label="Price limit"
				      amount={priceLimit.min != null
					      ? `min ${eur(totalOf(priceLimit.min, days))}`
					      : `max ${eur(totalOf(priceLimit.max, days))}`}
				      tone={priceLimit.applies ? 'amber' : 'neutral'}
				      note={priceLimit.applies
					      ? `APPLIES → ${eur(totalOf(limitCap?.to, days))}`
					      : 'does not apply'}
				      action={<SetupLink tab="price-limits" id={priceLimit.id}/>}>
					<SubNote>{priceLimit.label || 'unnamed limit'}</SubNote>
				</Line>
			)}
			{protections.map((p) => (
				<Line key={p.id || p.label}
				      label="Date protection"
				      amount={`min ${eur(totalOf(p.min, days))}`}
				      tone={p.applies ? 'amber' : 'neutral'}
				      note={p.applies ? `APPLIES → ${eur(totalOf(limitCap?.to ?? p.min, days))}` : outranked(p)}
				      action={<SetupLink tab="price-limits" id={p.id}/>}>
					<SubNote>
						<strong>{p.label}</strong> · {crossing(p)}
						{p.applies && ` · ${eur(p.min)}/day for the whole rental`}
					</SubNote>
				</Line>
			))}
		</SectionBox>
	)
}

function TetheringSection({ row, days }) {
	const tethers = row.tethers || []
	if (tethers.length === 0) return null
	return (
		<SectionBox title="Tethering"
		            caption={`${tethers.length} derived price${tethers.length === 1 ? '' : 's'}`}>
			{tethers.map((t, i) => {
				const pct = t.delta?.k === 'pct'
				const v = t.delta?.v ?? 0
				return (
					<Line key={i}
					      label={`${row.scope?.sipp || '—'} → ${t.child_value}`}
					      amount={eur(totalOf(t.price, days))}
					      note={pct ? `${v > 0 ? '+' : ''}${v} %`
					                : `${v >= 0 ? '+' : '−'} ${eur(Math.abs(v))}`}>
						<SubNote>
							{t.name || t.dimension}
							{t.out_of_order?.length > 0 && (
								<Box component="span" sx={{ color: dsColorNew.dangerRed, fontWeight: 700 }}>
									{' '}· out of order vs {t.out_of_order.join(', ')}
								</Box>
							)}
						</SubNote>
					</Line>
				)
			})}
		</SectionBox>
	)
}

function PushSection({ row, days }) {
	const daily = row.new_price ?? row.pushed_price
	if (daily == null) return null
	const writes = row.writes || []
	return (
		<SectionBox title="Final PMS push" caption={vatNote(row)}>
			{writes.length === 0 ? (
				<Line label="Price to set in the PMS" amount={eur(totalOf(daily, days))}
				      note={days ? `${eur(daily)} / day` : null}/>
			) : writes.map((w, i) => (
				<Line key={i} label={w.pricelist_code || w.pricelist_id || 'pricelist'}
				      amount={eur(totalOf(w.amount ?? daily, days))}
				      note={days ? `${eur(w.amount ?? daily)} / day` : null}/>
			))}
			<Stack direction="row" gap={1} flexWrap="wrap" alignItems="baseline"
			       sx={{ mt: 1 }}>
				{row.previous_price != null && (
					<SubNote>was {eur(totalOf(row.previous_price, days))}</SubNote>
				)}
				{row.previous_price != null && daily != null && (
					<Typography sx={{
						fontSize: 13, fontWeight: 700,
						fontVariantNumeric: 'tabular-nums',
						color: daily > row.previous_price ? dsColorNew.successText
						     : daily < row.previous_price ? dsColorNew.dangerRed : dsColorNew.textMuted,
					}}>
						{daily > row.previous_price ? '+' : ''}
						{(totalOf(daily, days) - totalOf(row.previous_price, days)).toFixed(2)} €
					</Typography>
				)}
				{row.simulated && <SubNote>· dry run — nothing written</SubNote>}
			</Stack>
		</SectionBox>
	)
}

/** The market as the engine saw it, kept with the event. */
function CompetitorsDialog({ open, onClose, row, days, competitors, ours = null }) {
	// The insurance arrives as an id; its catalogue lives here, not in
	// the webback's name loader.
	const axes = useAxisCatalog()
	const anchor = row.derivation?.reference_provider_id
	//: Whether that anchor is the dearest offer rather than the one at
	//: the target position. Only the incomplete-market branch does that.
	const dearestAnchor = row.derivation?.incomplete_market != null
	// Our own row sits where its price would in the rate shop: the market
	// snapshot keeps the competitors only (ours is the one the engine
	// prices), and a list of rivals without us in it does not say whether
	// we are first or last (Curro, 2026-10-03). No position is recorded
	// for ours, so it is placed by daily price among them.
	// Cheapest first (Curro, 2026-10-03). Rank is close to price order but
	// not the same thing — it is the position in the whole rate shop, every
	// category together, so a subset of it can read out of order. An offer
	// with no price goes last rather than to the top.
	const byPrice = (a, b) => (a.daily_price == null ? Infinity : Number(a.daily_price))
	                        - (b.daily_price == null ? Infinity : Number(b.daily_price))
	const rivals = [...competitors].sort(byPrice)
	// Our real position, when the decision recorded it (engine ≥ 2026-10-03);
	// older events place us by price instead.
	const ownPosition = row.derivation?.own_position ?? null
	// Our kind is the kind of the row the engine took (CU-869fh646w,
	// `derivation.own`): the like-with-like filter judges our own row,
	// not the rate card's axes. Three generations of events: `own` since
	// this task, `scope_filter` since 2026-10-08 (its kind is the row's
	// too), the scope before that.
	const sf = row.derivation?.scope_filter || null
	const own = row.derivation?.own && typeof row.derivation.own === 'object' ? row.derivation.own : null
	const compare = row.derivation?.compare || null
	const sorted = ours == null ? rivals : (() => {
		const mine = { ours: true, provider: 'Our price', daily_price: ours, position: ownPosition,
		               total_price: totalOf(ours, days),
		               is_prepaid: own && own.tariff_type != null ? Boolean(Number(own.tariff_type))
		                         : sf ? Boolean(Number(sf.is_prepaid)) : (row.scope?.is_prepaid ?? null),
		               car: own?.car || row.derivation?.own_car || null,
		               insurance_id: own && own.insurance != null ? own.insurance
		                           : sf ? sf.insurance : (row.scope?.insurance_id ?? null) }
		const at = rivals.findIndex(c => c.daily_price != null && Number(c.daily_price) > Number(ours))
		return at < 0 ? [...rivals, mine] : [...rivals.slice(0, at), mine, ...rivals.slice(at)]
	})()
	return (
		// `lg`, not `md`: the car and the insurance took it to eight
		// columns and the heads started wrapping (Curro, 2026-10-03).
		<AppDialog open={open} onClose={onClose} maxWidth="lg" paperSx={setupScopeSx}>
			<AppDialogHeader icon="users" title="Competitors in scope" onClose={onClose}
			                 subtitle={<>
					{row.scope?.sipp} · {row.scope?.pickup_date} · {days} days ·
					{/* Who counted as a competitor: our own row's kind, per
					    switch (CU-869fh646w). An event before the switches keeps
					    the sentence it always had; one before any kind, none. */}
					{compareCopy(sf, own, compare, axes) && ` ${compareCopy(sf, own, compare, axes)} ·`}
					{' '}recorded with the decision, not fetched — the rate shop
					{' '}itself is kept for 35 days and this event is not.
			                 </>}/>
			<AppDialogBody sx={dialogBodySx}>
				<Table size="small">
					<TableHead>
						<TableRow>
							{/* The rank the strategy counts: Ranking orders the offers in
							    scope by price and "position N" is the N-th of that list;
							    it never read the rate shop's own rank (UX, 2026-10-07). */}
							<HeadCell width={64} title="Position among the offers in scope, cheapest first — the one the strategy counts. The rate shop's own rank, every category together, is not this number.">Rank</HeadCell>
							<HeadCell>Provider</HeadCell>
							{/* An ACRISS is a class, so two offers in it can be a Fiat
							    500 and a Mini and the gap is the car. Recorded with the
							    decision since 2026-10-03 (Thibault). */}
							<HeadCell>Car</HeadCell>
							<HeadCell width={140}>Web supplier</HeadCell>
							<HeadCell width={130}>Insurance</HeadCell>
							<HeadCell width={100}>Tariff type</HeadCell>
							<HeadCell align="right" width={110}>Total</HeadCell>
							<HeadCell align="right" width={110}>Per day</HeadCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{sorted.map((c, i) => {
							// The anchor is ONE row — the competitor the policy priced
							// against — but the snapshot names rows by provider and a
							// broker lists several offers in one category, so the badge
							// used to light every discovercars row (Curro, 2026-10-03).
							// The reference price tells them apart.
							const ref = row.derivation?.reference
							const isAnchor = !c.ours && c.provider_id === anchor
								&& (ref == null || c.daily_price == null
								    || Math.abs(Number(c.daily_price) - Number(ref)) < 0.005)
							return (
								<TableRow key={i}
								          sx={{ bgcolor: c.ours ? dsColorNew.noteAmberBg : isAnchor ? dsColorNew.pillActiveBg : 'transparent' }}>
									<TableCell sx={{ ...cellPadSx, fontWeight: c.ours ? 800 : 400 }}>{i + 1}</TableCell>
									<TableCell sx={cellPadSx}>
										{/* One line: a label long enough to wrap used to break
										    through its own pill and double the row's height. The
										    long version lives in the tooltip. */}
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75,
										           whiteSpace: 'nowrap' }}>
											<AppBrandMark name={c.provider}/>
											<Typography sx={{ fontSize: 13,
											                  fontWeight: (isAnchor || c.ours) ? 800 : 500 }}>
												{c.provider}
											</Typography>
											{c.ours && (
												<Chip size="small" label="ours"
												      title="Our own offer, at the price it had before this decision"
												      sx={{ height: 19, fontSize: 11, fontWeight: 700,
												            letterSpacing: '0.04em', textTransform: 'uppercase',
												            bgcolor: dsColorNew.noteAmber, color: dsColorNew.white,
												            '& .MuiChip-label': { px: 0.8 } }}/>
											)}
											{isAnchor && (
												// In a thin market the anchor is the DEAREST offer, not
												// the one at the target position — saying only "anchor"
												// next to the most expensive row reads like a bug.
												<Chip size="small"
												      label={dearestAnchor ? 'anchor \u00b7 dearest' : 'anchor'}
												      title={dearestAnchor
												             ? 'Nobody held the target position, so the strategy '
												               + 'priced above the dearest offer in the market'
												             : 'The one offer the strategy priced against'}
												      sx={{ height: 19, fontSize: 11, fontWeight: 700,
												            letterSpacing: '0.04em', textTransform: 'uppercase',
												            bgcolor: dsColorNew.appBlue, color: dsColorNew.white,
												            '& .MuiChip-label': { px: 0.8 } }}/>
											)}
										</Box>
									</TableCell>
									<TableCell sx={cellPadSx}>
										<Typography sx={{ fontSize: 13,
										                  color: c.car ? dsColorNew.textTitle : dsColorNew.textSubtle,
										                  fontWeight: c.ours ? 700 : 400 }}>
											{c.car || '—'}
										</Typography>
									</TableCell>
									<TableCell sx={cellPadSx}>
										<Typography sx={{ fontSize: 13,
										                  color: dsColorNew.textMuted }}>
											{c.web_supplier || '—'}
										</Typography>
									</TableCell>
									<TableCell sx={cellPadSx}>
										<Typography sx={{ fontSize: 13,
										                  color: c.insurance_id == null ? dsColorNew.textSubtle : dsColorNew.textMuted }}>
											{c.insurance_id == null ? '—' : axes.insuranceName(c.insurance_id)}
										</Typography>
									</TableCell>
									<TableCell sx={cellPadSx}>
										<Typography sx={{ fontSize: 13,
										                  color: dsColorNew.textMuted }}>
											{axes.prepaidName(c.is_prepaid == null ? null : Boolean(c.is_prepaid))}
										</Typography>
									</TableCell>
									<TableCell align="right" sx={cellPadSx}>
										<Typography sx={{ fontSize: 13, fontWeight: 700,
										                  fontVariantNumeric: 'tabular-nums' }}>
											{eur(c.total_price ?? totalOf(c.daily_price, days))}
										</Typography>
									</TableCell>
									<TableCell align="right" sx={cellPadSx}>
										<Typography sx={{ fontSize: 13,
										                  color: dsColorNew.textMuted,
										                  fontVariantNumeric: 'tabular-nums' }}>
											{eur(c.daily_price)}
										</Typography>
									</TableCell>
								</TableRow>
							)
						})}
					</TableBody>
				</Table>
				{excludedCopy(sf, compare) && (
					<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1.5 }}
					            title={EXCLUDED_TOOLTIP}>
						{excludedCopy(sf, compare)}
					</Typography>
				)}
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
			</AppDialogFooter>
		</AppDialog>
	)
}

/** The rail, as the answer to "how did you get that number". */
function CalculationDialog({ open, onClose, row }) {
	return (
		<AppDialog open={open} onClose={onClose} maxWidth="sm" paperSx={setupScopeSx}>
			<AppDialogHeader icon="calculator" title="How this was calculated" onClose={onClose}
			                 subtitle="Totals for the rental. The engine decides in daily rates and the conversion is a ratio, so every step holds either way; the result is shown in both."/>
			<AppDialogBody sx={dialogBodySx}>
				<DerivationRail row={row}/>
			</AppDialogBody>
			<AppDialogFooter>
				<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
			</AppDialogFooter>
		</AppDialog>
	)
}

/**
 * The event, in the order the questions get asked.
 *
 * Replaces the single chain that shipped this morning: the chain is
 * still here, behind the ⓘ on "Price we decided", because that is
 * exactly the question it answers and it answers it in daily terms.
 * Everything on this level is a total for the rental.
 */
function DetailDerivation({ row }) {
	if (row.via_tether) return <TetherDerivation row={row}/>
	const days = row.scope?.duration_days
	return (
		<Stack gap={1.5}>
			<MarketSection row={row} days={days}/>
			<StrategySection row={row} days={days}/>
			<LimitsSection row={row} days={days}/>
			<TetheringSection row={row} days={days}/>
			<PushSection row={row} days={days}/>
		</Stack>
	)
}

/**
 * The loud one. Above every section, because it is not a step in the
 * calculation — it is the calculation refusing to satisfy two rules
 * that contradict.
 *
 * The category ladder restores order by lowering the cheaper car onto
 * the dearer one. A floor forbids going under a number. When the
 * cheaper car's floor sits above the dearer car's price, there is no
 * price that satisfies both, so the floor holds and the pair ships out
 * of order. The price DID go out — that is why this is a warning and
 * not an error — but somebody has to decide whether the floor or the
 * grouping is wrong, and nothing else on this screen would tell them.
 */
function FloorVsOrderWarning({ row }) {
	const blocked = row.payload?.consistency_blocked_by_floor
	if (!blocked?.length) return null
	return (
		<AppNote severity="warning">
			<Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>
				Pushed out of order: a floor would not yield
			</Typography>
			<Stack gap={0.75} sx={{ mt: 1 }}>
				{blocked.map((b, i) => (
					<Box key={i} sx={{ fontSize: 13, lineHeight: 1.5 }}>
						<strong>{b.value_code}</strong> held at{' '}
						<strong>{eur(b.held_at)}</strong> because its floor is{' '}
						<strong>{eur(b.floor)}</strong>, which is above{' '}
						<strong>{b.reference_acriss}</strong> at{' '}
						<strong>{eur(b.reference_price)}</strong>.
						<Box component="span" sx={{ opacity: 0.85 }}>
							{' '}Group “{b.group_name}” is therefore inconsistent until
							the floor comes down or the two stop sharing a ladder.
						</Box>
					</Box>
				))}
			</Stack>
		</AppNote>
	)
}

function SpreadVsOrderWarning({ row }) {
	const lines = spreadWarningLines(row)
	if (!lines.length) return null
	return (
		<AppNote severity="warning">
			<Typography sx={{ fontWeight: 700, fontSize: 13, mb: 0.5 }}>
				Spread vs order: this child kept its tether and left its group out of order
			</Typography>
			<Stack gap={0.75} sx={{ mt: 1 }}>
				{lines.map((l, i) => (
					<Box key={i} sx={{ fontSize: 13, lineHeight: 1.5 }}>
						<strong>{row.scope?.sipp}</strong> {l.text}.
						<Box component="span" sx={{ opacity: 0.85 }}>
							{' '}Group “{l.group}” is therefore inconsistent until the spread
							changes or the two stop sharing a ladder.
						</Box>
					</Box>
				))}
			</Stack>
			<Stack direction="row" gap={1} sx={{ mt: 1 }}>
				<SetupLink tab="tethering" label="Change the spread"/>
				<SetupLink tab="category-consistency" label="Change the group"/>
			</Stack>
		</AppNote>
	)
}

function WriteBlock({ write }) {
	const [showJson, setShowJson] = useState(false)
	const ok = write.status === 'ok'
	return (
		<Box sx={{
			bgcolor: dsColorNew.white,
			border: '1px solid',
			borderColor: ok ? alpha(dsColorNew.successBar, 0.4) : dsColorNew.dangerBorder,
			borderLeft: '4px solid',
			borderLeftColor: ok ? dsColorNew.successText : dsColorNew.dangerRed,
			borderRadius: 1, p: 1,
		}}>
			<Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
				<Chip size="small"
				      label={ok ? 'OK' : 'ERROR'}
				      sx={toneChipSx(ok ? 'ok' : 'err')}/>
				<Box component="code" sx={{ fontFamily: 'inherit',
				                            fontSize: 12, fontWeight: 700 }}>
					{write.pricelist}
				</Box>
				<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
				                  fontFamily: 'inherit' }}>
					{write.api}
				</Typography>
				<Box sx={{ flex: 1 }}/>
				<Button variant="text" size="small" onClick={() => setShowJson((v) => !v)}
				        sx={{ fontWeight: 600, fontSize: 12 }}>
					{showJson ? 'Hide JSON' : 'Show JSON'}
				</Button>
			</Stack>
			<Collapse in={showJson} unmountOnExit>
				<Stack direction="row" gap={1} sx={{ mt: 1 }}>
					<JsonBox title="Request"  data={write.request}/>
					<JsonBox title="Response" data={write.response}/>
				</Stack>
			</Collapse>
		</Box>
	)
}

function JsonBox({ title, data }) {
	return (
		<Box sx={{ flex: 1, bgcolor: dsColorNew.navy, color: dsColorNew.grayBorder,
		           borderRadius: 1, overflow: 'hidden' }}>
			<Typography sx={{ fontSize: 11, fontWeight: 700,
			                  letterSpacing: '0.06em', textTransform: 'uppercase',
			                  px: 1, py: 0.5, bgcolor: dsColorNew.textTitle, color: dsColorNew.noteInfoBorder }}>
				{title}
			</Typography>
			<Box component="pre" sx={{
				m: 0, p: 1, fontFamily: 'ui-monospace, monospace',
				fontSize: 11, lineHeight: 1.4,
				overflow: 'auto', maxHeight: 220,
			}}>
				{JSON.stringify(data, null, 2)}
			</Box>
		</Box>
	)
}

/**
 * What can still be done about a cap, and what already was.
 *
 * It used to open by repeating the limit — which now sits in the
 * Limits section, above the push where it belongs and stated whether
 * or not it fired. Below the push it read as the cap happening after
 * the price was sent (Curro, 2026-09-28). What is left is genuinely
 * after the fact: the resolutions, and the two things an operator can
 * do next.
 */
function DetailCapped({ row }) {
	const ov = overridable(row.cap_chain)
	return (
		<Box sx={detailBoxSx}>
			<DetailTitle>Resolution</DetailTitle>
			{row.resolution === 'manual_override' && (
				<Box sx={{ p: 1, bgcolor: dsColorNew.noteAmberBg, border: `1px solid ${dsColorNew.noteAmberBorder}`,
				           borderRadius: 1, mb: 1 }}>
					<Typography sx={{ fontWeight: 700, color: dsColorNew.noteAmber,
					                  fontSize: 12 }}>
						Overridden{row.resolution_actor ? ` by ${row.resolution_actor}` : ''}
						{row.resolution_at ? ` · ${hhmm(row.resolution_at)}` : ''} — the price before the cap
						was pushed for this one hit.
					</Typography>
					{row.resolution_note && (
						<Typography sx={{ fontSize: 12, color: dsColorNew.noteAmber, mt: 0.5 }}>
							Reason: {row.resolution_note}
						</Typography>
					)}
				</Box>
			)}
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				<strong>The change was pushed to the PMS</strong> — at the limit value,
				not at the proposed.
				{!row.resolution && ov.can && <> Use <strong>Override</strong> to push the
					original proposed price now, for this one hit; the next pass caps again.</>}
				{!row.resolution && ov.onlyConsistency && <> It was kept in order by its
					category consistency group, which an override does not jump — change the
					group in Setup if the order is wrong.</>}
			</Typography>
		</Box>
	)
}

function DetailQuarantined({ row }) {
	// `current_price` was a field of the mock era that the server has
	// never sent, so this block read "Current price: undefined€" on
	// every held decision — and Thibault concluded a cell had been
	// quarantined *because* its price was undefined. It had not: it
	// was a rise of 85 % over the tenant's 80 % threshold, which the
	// block did not say either (2026-10-12).
	const prev = row.previous_price
	const held = row.new_price ?? row.pushed_price
	const d = row.delta ?? (held != null && prev != null ? held - prev : null)
	return (
		<Box sx={detailBoxSx}>
			<DetailTitle>Quarantine — held, nothing sent</DetailTitle>
			<Stack direction="row" gap={3} flexWrap="wrap" sx={{ mb: 1.5 }}>
				<KV k="Price in the PMS" v={prev != null ? `${prev.toFixed(2)}€` : null}/>
				<KV k="Held price" v={held != null ? `${held.toFixed(2)}€` : null}
				    highlight={d > 0 ? 'up' : d < 0 ? 'down' : null}/>
				<KV k="Would-be delta"
				    v={d == null ? null : `${d > 0 ? '+' : ''}${d.toFixed(2)}€`}/>
			</Stack>
			<Box sx={{ p: 1, bgcolor: dsColorNew.noteInfoBg, border: `1px solid ${dsColorNew.noteInfoBorder}`,
			           borderRadius: 1, mb: 1 }}>
				<Typography sx={{ fontWeight: 700, color: dsColorNew.appBlue,
				                  fontSize: 13 }}>
					The engine did everything except push
				</Typography>
				<Typography sx={{ fontSize: 12, color: dsColorNew.appBlue, mt: 0.5 }}>
					{row.simulated
						? 'Simulation: the price change was computed and logged, and no request was sent to the PMS.'
						: 'The move was further than this tenant allows, so it waits for a human: approve it to push the held price, or discard it and let the next pass propose again.'}
				</Typography>
			</Box>

			{/* What held it, in the engine's own numbers (TL D3, CU-869fe8jrk):
			    the rule, the threshold, and — when a date protection set the
			    floor — the baseline the move was measured from, so the step
			    up to the floor is not read as a jump to approve (Duda 4 → b). */}
			{(row.quarantine || row.quarantine_baseline) && (
				<Box sx={{ p: 1, bgcolor: dsColorNew.noteInfoBg, border: `1px solid ${dsColorNew.noteInfoBorder}`,
				           borderRadius: 1, mb: 1 }}>
					{row.quarantine && (
						<Typography sx={{ fontSize: 12, color: dsColorNew.appBlue }}>
							Held by <strong>{row.quarantine.rule}</strong>: {row.quarantine.value} against a
							threshold of {row.quarantine.threshold}
							{row.quarantine.baseline && row.quarantine.baseline !== row.quarantine.previous_price
								? `, measured from ${Number(row.quarantine.baseline).toFixed(2)} € and not from the previous ${Number(row.quarantine.previous_price).toFixed(2)} €`
								: ''}.
						</Typography>
					)}
					{row.quarantine_baseline && (
						<Typography sx={{ fontSize: 12, color: dsColorNew.appBlue, mt: row.quarantine ? 0.5 : 0 }}>
							Measured after the protection: from {Number(row.quarantine_baseline.protection_floor).toFixed(2)} €
							(the floor <strong>{row.quarantine_baseline.label}</strong> sets)
							{row.new_price != null ? ` to ${Number(row.new_price).toFixed(2)} €` : ''}.
							{row.quarantine_baseline.previous_price != null
								&& ` The ${Number(row.quarantine_baseline.previous_price).toFixed(2)} → ${Number(row.quarantine_baseline.protection_floor).toFixed(2)} € step is the protection's own and is not measured against the thresholds.`}
						</Typography>
					)}
				</Box>
			)}

			{row.resolution === 'applied_manually' && (
				<Box sx={{ p: 1, bgcolor: dsColorNew.successBg, border: `1px solid ${alpha(dsColorNew.successBar, 0.4)}`,
				           borderRadius: 1, mb: 1 }}>
					<Typography sx={{ fontWeight: 700, color: dsColorNew.successText,
					                  fontSize: 12 }}>
						Applied manually — a fresh Applied event was emitted.
					</Typography>
				</Box>
			)}

			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>
				Use <strong>Apply manually</strong> to push the proposed price now,
				or <strong>Discard</strong> to leave the current price untouched.
			</Typography>
		</Box>
	)
}

function DetailReason({ row }) {
	// Totals for the stay, like every other block (Curro, 2026-09-28).
	const days = row.scope?.duration_days
	// The thin-market exits decline to price, and a refusal the operator
	// cannot check is a refusal he has to take on trust: the block states
	// the comparison that produced it, with both figures (UX gate,
	// criterion 5). Every other reason is already a sentence.
	const im = incompleteMarketNoChange(row, days)
	// `no_strategy` has two sentences since CU-869fe6mf4 — nobody covers
	// the cell, or nobody covers this face of a covered cell — and they
	// lead to different actions, so they are composed here with the face's
	// name rather than kept in `REASON_TEXT`, like the thin-market ones.
	const faceName = useFaceLabel(row)
	const ns = canonicalEventType(row.event_type) === 'no_strategy' ? noStrategyCopy(row, faceName) : null
	return (
		<Box sx={detailBoxSx}>
			{/* `dense` because the row owns the gap: the title's own
			    bottom margin inside a Stack that also has one doubled the
			    space under REASON on every event with a reason, this one
			    or not (QA, 2026-10-05). */}
			<Stack direction="row" alignItems="center" gap={1} sx={{ mb: 0.9 }}>
				<DetailTitle dense>Reason</DetailTitle>
				{im && (
					<Chip size="small" label={im.badge} sx={toneChipSx('info')}/>
				)}
				<Box sx={{ flex: 1 }}/>
				{im && <InfoIcon sx={{ fontSize: 17, color: dsColorNew.textSubtle }}
				                 titleAccess={im.tooltip}/>}
			</Stack>
			<Typography sx={{ fontSize: 13, color: dsColorNew.textTitle,
			                  lineHeight: 1.6 }}>
				{im?.reason || ns?.reason || row.reason || 'The engine recorded no reason for this one.'}
			</Typography>
			{im && (
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
				                  lineHeight: 1.6, mt: 0.6 }}>
					{im.detail}
				</Typography>
			)}
			{ns?.detail && (
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
				                  lineHeight: 1.6, mt: 0.6 }}>
					{ns.detail}
				</Typography>
			)}
			{/* The filter left nothing to compare against, so there is no
			    popup with a footer to say what it left out: it is said
			    here instead (UX gate § 5, 2026-10-07). */}
			{!(row.derivation?.competitors?.length) && excludedCopy(row.derivation?.scope_filter, row.derivation?.compare) && (
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted,
				                  lineHeight: 1.6, mt: 0.6 }}
				            title={EXCLUDED_TOOLTIP}>
					{excludedCopy(row.derivation?.scope_filter, row.derivation?.compare)}
				</Typography>
			)}
			{row.previous_price != null && (
				<Typography sx={{ fontSize: 13, color: dsColorNew.textMuted, mt: 0.5 }}>
					Price in the PMS: <strong>{row.previous_price?.toFixed(2)}€</strong>
				</Typography>
			)}
		</Box>
	)
}

// ---------------------------------------------------------------------------
// Filter logic
// ---------------------------------------------------------------------------
// `applyFilters` lived here and filtered the rows the browser held.
// The server filters now — see `@services/useActivity` — because a
// page of thirty rows is not something you can search.

// ---------------------------------------------------------------------------
// Small primitives
// ---------------------------------------------------------------------------
const detailBoxSx = {
	bgcolor: dsColorNew.white,
	border: '1px solid', borderColor: dsColorNew.grayBorder,
	borderRadius: 1, p: 1.5,
}
function DetailTitle({ children, dense = false }) {
	return (
		<Typography sx={{ fontSize: 12, fontWeight: 700,
		                  textTransform: 'uppercase', letterSpacing: '0.06em',
		                  color: dsColorNew.textMuted, mb: dense ? 0 : 0.9 }}>
			{children}
		</Typography>
	)
}
function KV({ k, v, highlight }) {
	const colorByHighlight = { up: dsColorNew.successText, down: dsColorNew.dangerRed }
	return (
		<Box sx={{ display: 'inline-flex', flexDirection: 'column', gap: 0.15, mr: 3 }}>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted,
			                  textTransform: 'uppercase', letterSpacing: '0.04em',
			                  fontWeight: 700 }}>
				{k}
			</Typography>
			<Typography sx={{ fontSize: 16, fontWeight: 700,
			                  color: highlight ? colorByHighlight[highlight] : dsColorNew.textTitle }}>
				{v ?? '—'}
			</Typography>
		</Box>
	)
}
// A figure card that must not be missed: red edge and wash, and a slow pulse, while it is not zero
function criticalSx(on) {
	return on ? {
		borderColor: dsColorNew.dangerRed, bgcolor: dsColorNew.dangerBg,
		animation: 'dsPulseCritical 1.6s ease-in-out infinite',
		'@keyframes dsPulseCritical': {
			'0%, 100%': { boxShadow: `0 0 0 0 ${dsColorNew.dangerBorder}` },
			'50%':      { boxShadow: `0 0 0 6px transparent` },
		},
	} : {}
}
function HeadCell({ children, sx = {}, ...rest }) {
	return (
		<TableCell {...rest}
		           sx={{ ...headCellSx, ...sx,
		                 // Two-word heads wrapped and made the head twice as
		                 // tall as it needed to be ("Web supplier").
		                 whiteSpace: 'nowrap',
		                 py: 1.25 }}>
			{children}
		</TableCell>
	)
}
